import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Card,
  CardContent,
  Button,
  TextField,
  CircularProgress,
  Alert,
  AlertTitle,
  Snackbar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ButtonGroup,
  IconButton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  SkipNext as SkipNextIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
  LocationOn as LocationOnIcon,
  Add as AddIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as FitScreenIcon,
  RotateRight as RotateRightIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { ReportData } from '../types';
import DocumentWithHighlights from './DocumentWithHighlights';

interface DataViewerSideBySideProps {
  reportData: ReportData;
  onClose?: () => void;
  onSave?: (updatedData: any) => void;
  onSaveAndNext?: () => void;
  remainingCount?: number;  // # of items still to review in the current filter (excludes this one)
}

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || 'https://your-api-gateway-url.execute-api.us-east-2.amazonaws.com/prod';

const DataViewerSideBySide: React.FC<DataViewerSideBySideProps> = ({ reportData, onClose, onSave, onSaveAndNext, remainingCount = 0 }) => {
  const { getIdTokenClaims } = useAuth0();
  // Crude tickets (SGS KDR) reuse this whole viewer shell (document panel, zoom,
  // Auto-Find, save/verify/next) but render a generic field tree instead of the
  // proving-report layout, and route saves to the CrudeTickets table.
  const isTicket = (reportData as any)?.document_type === 'crude_ticket';
  const typeQuery = isTicket ? '?type=ticket' : '';
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState<any>({});
  const [imageUrl, setImageUrl] = useState<string>('');
  const [imageLoading, setImageLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(0.5);
  const [isInitialized, setIsInitialized] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [fieldToFocus, setFieldToFocus] = useState<string | null>(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [scrollStart, setScrollStart] = useState({ x: 0, y: 0 });
  const [saveStatus, setSaveStatus] = useState<{ show: boolean; message: string; severity: 'success' | 'error' }>({
    show: false,
    message: '',
    severity: 'success'
  });

  // Add Field Dialog state
  const [addFieldDialog, setAddFieldDialog] = useState(false);
  const [newFieldSection, setNewFieldSection] = useState('root');
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldValue, setNewFieldValue] = useState('');

  // Image rotation state
  const [rotation, setRotation] = useState(0);

  // Track if we just saved to prevent resetting data
  const [justSaved, setJustSaved] = useState(false);

  // Page-image navigation: any doc that ships per-page PNGs (reportData.page_image_keys)
  // gets the Auto-Find overlay on an <img> + page nav. This covers crude tickets AND
  // single-document proving reports (multi-mode provings keep their own per-page path).
  const docPageKeys: string[] = (reportData as any)?.page_image_keys || [];
  const docTotalPages: number = (reportData as any)?.total_pages || docPageKeys.length || 1;
  const [docPage, setDocPage] = useState(1);
  const [docPageUrl, setDocPageUrl] = useState('');

  // Handle potential nested structure
  let extractedData = reportData.extracted_data;
  if (extractedData && typeof extractedData === 'object' && 'extracted_data' in extractedData) {
    extractedData = (extractedData as any).extracted_data;
  }

  // Initialize edited data and local copy for display
  const [localExtractedData, setLocalExtractedData] = useState(extractedData);

  useEffect(() => {
    // Only update if we're not currently editing and not just after saving
    if (!isEditing && !justSaved) {
      // Handle potential nested structure
      let dataToUse = reportData.extracted_data;
      if (dataToUse && typeof dataToUse === 'object' && 'extracted_data' in dataToUse) {
        dataToUse = (dataToUse as any).extracted_data;
      }

      // Deep clone the data to avoid mutations
      const dataToEdit = JSON.parse(JSON.stringify(dataToUse || {}));
      setEditedData(dataToEdit);
      setLocalExtractedData(dataToUse);
    }

    // Reset the justSaved flag after the effect runs
    if (justSaved) {
      setJustSaved(false);
    }
  }, [reportData.extracted_data, isEditing, justSaved]); // Use the prop directly for dependency

  // Fetch a presigned URL for the current ticket page image whenever the page changes.
  useEffect(() => {
    if (docPageKeys.length === 0) return;
    const key = docPageKeys[docPage - 1];
    if (!key) return;
    let cancelled = false;
    (async () => {
      try {
        const tokenClaims = await getIdTokenClaims();
        const idToken = tokenClaims?.__raw;
        const resp = await axios.get(`${API_ENDPOINT}/image-url/${encodeURIComponent(key)}`, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        if (!cancelled && resp.data?.imageUrl) setDocPageUrl(resp.data.imageUrl);
      } catch (e) {
        console.error('Failed to load ticket page image:', e);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docPage, reportData.report_id]);

  // When a field is focused (hovered) in the data panel, jump the document to the page
  // that field was found on so its Auto-Find box is visible. Works for any page-image
  // doc (tickets + single-doc provings). Best-effort: composite/unresolvable keys just
  // don't jump.
  useEffect(() => {
    if (docPageKeys.length === 0 || !focusedField) return;
    // Resolve the focused dotted key (e.g. "net_outturn.m3_at_15c") to its leaf.page,
    // unwrapping any {value: {...}} containers along the way.
    const parts = focusedField.split('.');
    let node: any = localExtractedData;
    for (const p of parts) {
      if (node && typeof node === 'object' && node.value && typeof node.value === 'object') node = node.value;
      if (node && typeof node === 'object' && p in node) node = node[p];
      else { node = null; break; }
    }
    const pg = node && typeof node === 'object' ? node.page : undefined;
    if (typeof pg === 'number' && pg >= 1 && pg !== docPage) {
      setDocPage(pg);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedField]);

  // Automatically fetch document image on load
  useEffect(() => {
    fetchImageUrl();
  }, [reportData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get presigned URL for image viewing (prioritize page-specific image)
  const fetchImageUrl = useCallback(async () => {
    setImageLoading(true);
    try {
      // Priority order for multipage documents:
      // 1. source_page - Individual page PNG/PDF for display with bounding boxes
      // 2. source_image - Image version if available
      // 3. source_document - Original multipage document (fallback)
      // 4. s3_key - Legacy field
      const sourceImage = reportData.source_page ||  // New: Individual page image/PDF
                         extractedData?.source_page ||
                         (reportData as any).extracted_data?.source_page ||
                         reportData.source_image ||
                         extractedData?.source_image ||
                         (reportData as any).extracted_data?.source_image;

      // If no specific page image, fall back to original document
      // Priority: s3_original_key (actual file) > s3_key (base name) > s3_processed_key
      const s3Key = sourceImage ||
                    reportData.source_document ||  // New: Original multipage PDF
                    extractedData?.source_document ||
                    (reportData as any).extracted_data?.source_document ||
                    reportData.s3_original_key ||  // This has the actual file with page number
                    reportData.s3_key ||            // This might be the base name without page
                    reportData.s3_processed_key;

      if (!s3Key) {
        console.warn('No S3 key or source image found for document');
        return;
      }

      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;

      console.log(`Fetching image URL for: ${s3Key}`);
      const response = await axios.get(`${API_ENDPOINT}/image-url/${encodeURIComponent(s3Key)}`, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (response.data && response.data.imageUrl) {
        setImageUrl(response.data.imageUrl);
        console.log(`Successfully fetched image URL for page ${reportData.page_number || 'N/A'}`);
      }
    } catch (error) {
      console.error('Error fetching image URL:', error);
    } finally {
      setImageLoading(false);
    }
  }, [reportData, getIdTokenClaims]);

  // Helper to get field value with confidence
  // Helper function to convert date formats
  const convertDateToISO = (dateString: string): string => {
    if (!dateString) return '';

    // Check if already in yyyy-MM-dd format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }

    // Convert MM/DD/YY or MM/DD/YYYY to yyyy-MM-dd
    const parts = dateString.split('/');
    if (parts.length === 3) {
      let [month, day, year] = parts;
      // Pad month and day with zeros if needed
      month = month.padStart(2, '0');
      day = day.padStart(2, '0');
      // Convert 2-digit year to 4-digit
      if (year.length === 2) {
        const yearNum = parseInt(year);
        year = (yearNum > 50 ? '19' : '20') + year;
      }
      return `${year}-${month}-${day}`;
    }

    return dateString; // Return as-is if not recognized
  };

  const getFieldValue = (field: any): { value: any; confidence: number | null } => {
    if (field === null || field === undefined) {
      return { value: null, confidence: null };
    }

    // Handle DynamoDB Map wrapper first
    if (field && typeof field === 'object' && 'M' in field) {
      field = field.M;
    }

    if (typeof field === 'object' && 'value' in field && 'confidence' in field) {
      let value = field.value;
      let confidence = field.confidence;

      // Extract from DynamoDB format if needed
      if (value && typeof value === 'object') {
        if ('S' in value) value = value.S;
        else if ('N' in value) value = value.N;
        else if ('BOOL' in value) value = value.BOOL;
        else if ('M' in value) value = JSON.stringify(value.M);
        else if ('L' in value) value = value.L; // Return the list array directly
        else if (!Array.isArray(value)) {
          // Plain object without DynamoDB wrappers (but not an array) - stringify it
          value = JSON.stringify(value);
        }
      }

      // Extract confidence from DynamoDB format
      if (confidence && typeof confidence === 'object') {
        if ('N' in confidence) confidence = parseFloat(confidence.N);
        else if ('S' in confidence) confidence = parseFloat(confidence.S);
      }

      return { value, confidence };
    }

    // Old format or plain value - extract from DynamoDB format
    let value = field;
    if (value && typeof value === 'object') {
      if ('S' in value) value = value.S;
      else if ('N' in value) value = value.N;
      else if ('BOOL' in value) value = value.BOOL;
      else if ('M' in value) value = JSON.stringify(value.M);
      else if ('L' in value) value = value.L; // Return the list array directly
      else if (!Array.isArray(value)) {
        // Plain object without DynamoDB wrappers (but not an array) - stringify it
        value = JSON.stringify(value);
      }
    }

    return { value, confidence: null };
  };

  // Helper function to render confidence chip
  const renderConfidenceChip = (confidence: number | null) => {
    if (confidence === null || confidence === undefined) return null;

    const color = confidence >= 0.8 ? 'success' : confidence >= 0.6 ? 'warning' : 'error';
    const label = `${Math.round(confidence * 100)}%`;

    return (
      <Chip
        label={label}
        size="small"
        color={color}
        sx={{ ml: 1 }}
      />
    );
  };

  // Helper to clean OCR errors in real-time
  const cleanOCRErrors = (value: string, fieldName?: string): string => {
    if (!value || typeof value !== 'string') return value;

    // Apply known OCR corrections for meter IDs
    if (fieldName === 'meter_id') {
      return value
        .replace(/\bBAN\b/g, 'BAY')
        .replace(/\bARN\b/g, 'ARM')
        .replace(/\bABN\b/g, 'ARM')
        .replace(/\bBTH\b/g, 'ETH')
        .replace(/\bBIH\b/g, 'ETH');
    }

    // Remove Excel errors
    if (value.includes('#DIV/0') || value.includes('#VALUE!')) {
      // Try to extract the valid part if it exists
      const match = value.match(/(\d+\.?\d*)/);
      return match ? match[1] : '-';
    }

    return value;
  };

  // Helper to render field with confidence
  // Read the current (possibly edited) value at a dotted path, mirroring
  // handleFieldChange's traversal (descends through {value: ...} wrappers).
  const getNestedValue = (path: string): any => {
    const parts = path.split('.');
    let cur: any = editedData;
    for (let i = 0; i < parts.length; i++) {
      if (cur == null) return undefined;
      if (typeof cur === 'object' && 'value' in cur && typeof cur.value === 'object' && cur.value !== null) {
        cur = cur.value;
      }
      cur = cur[parts[i]];
    }
    if (cur && typeof cur === 'object' && 'value' in cur) return cur.value;
    return cur;
  };

  // True when this report is an SGS multi-flow report: either tagged
  // report_format == "SGS" or it carries a top-level flow_rate_averages array.
  const isSgsReport = (): boolean => {
    const rf = (
      getFieldValue((localExtractedData as any)?.report_format).value ||
      getFieldValue((localExtractedData as any)?.format).value || ''
    ).toString().toUpperCase();
    if (rf === 'SGS') return true;
    const raw = (localExtractedData as any)?.flow_rate_averages;
    const arr = Array.isArray(raw) ? raw : (Array.isArray(raw?.value) ? raw.value : []);
    return arr.length > 0;
  };

  // The exact fields that make up each per-flow MeterFactorHistory track record,
  // in the order the aggregator flattens them (see flatten_sgs_report_for_track).
  // `required` mirrors the review gate (per-flow MF + temperature + pressure).
  const SGS_FLOW_FIELDS: { key: string; label: string; required?: boolean }[] = [
    { key: 'average_meter_factor', label: 'Meter Factor (Ave-Met-Factor)', required: true },
    { key: 'flow_rate', label: 'Flow Rate', required: true },
    { key: 'temperature', label: 'Meter Temperature', required: true },
    { key: 'pressure', label: 'Meter Pressure', required: true },
    { key: 'repeatability', label: 'Repeatability' },
    { key: 'orig_correction', label: 'Orig Correction' },
    { key: 'final_correction', label: 'Final Correction' },
    { key: 'pre_calib_percent', label: 'Pre-Calib %' },
    { key: 'post_calib_percent', label: 'Post-Calib %' },
  ];

  // Renders the per-flow HIGH / LOW track cards inside the KEY FIELDS section.
  // This is the user's preview of exactly what each MeterFactorHistory record
  // will contain (one record per flow). Fields are editable via handleFieldChange
  // with `flow_rate_averages.<index>.<field>` paths.
  const renderSgsFlowTracks = (): any => {
    // flow_rate_averages is {value: [...], textract_confidence} (NO `confidence`
    // sibling), so getFieldValue would stringify it — read the array directly.
    const raw = (localExtractedData as any)?.flow_rate_averages;
    const flows: any[] = Array.isArray(raw) ? raw : (Array.isArray(raw?.value) ? raw.value : []);
    if (!flows.length) return null;

    const rateOf = (it: any) => {
      const r = it && typeof it === 'object'
        ? (it.flow_rate && typeof it.flow_rate === 'object' ? it.flow_rate.value : it.flow_rate)
        : undefined;
      const n = Number(r);
      return isNaN(n) ? null : n;
    };
    const rates = flows.map(rateOf);
    const valid = rates.filter((r) => r !== null) as number[];
    const minR = valid.length ? Math.min(...valid) : null;
    const maxR = valid.length ? Math.max(...valid) : null;

    return (
      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 1, mb: 0.5 }}>
          Flow-Specific Values
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Each card is a separate flow. Double-click any value to edit.
        </Typography>
        <Grid container spacing={2}>
          {flows.map((flow: any, i: number) => {
            const rate = rates[i];
            let fl = '';
            if (valid.length >= 2 && rate !== null) {
              if (rate === minR) fl = 'LOW';
              else if (rate === maxR) fl = 'HIGH';
            }
            const headerColor = fl === 'HIGH' ? 'error.main' : fl === 'LOW' ? 'info.main' : 'text.primary';
            const headerLabel = fl ? `${fl} FLOW` : `Flow ${i + 1}`;
            return (
              <Grid item xs={12} md={6} key={i}>
                <Box sx={{ p: 2, border: '2px solid', borderColor: headerColor, borderRadius: 1, bgcolor: 'background.paper' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: headerColor, mb: 1 }}>
                    {headerLabel}{rate != null ? ` — ${rate}` : ''}
                  </Typography>
                  <Grid container spacing={1}>
                    {SGS_FLOW_FIELDS.map(({ key, label, required }) => {
                      const fv = getFieldValue(flow?.[key]);
                      const path = `flow_rate_averages.${i}.${key}`;
                      const editVal = getNestedValue(path);
                      const displayVal = (fv.value === null || fv.value === undefined || fv.value === '')
                        ? '—'
                        : (typeof fv.value === 'object' ? JSON.stringify(fv.value) : fv.value);
                      return (
                        <Grid
                          item
                          xs={6}
                          key={key}
                          onMouseEnter={() => setFocusedField(path)}
                          onMouseLeave={() => setFocusedField(null)}
                          onDoubleClick={() => {
                            if (!isEditing) {
                              setIsEditing(true);
                              setFieldToFocus(path);
                            }
                          }}
                          sx={{ cursor: !isEditing ? 'pointer' : 'default' }}
                        >
                          <Typography
                            variant="caption"
                            color={required ? 'error.main' : 'text.secondary'}
                            sx={{ fontWeight: required ? 700 : 400, display: 'block' }}
                          >
                            {label}{required ? ' *' : ''}
                          </Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              variant="outlined"
                              value={editVal === null || editVal === undefined ? '' : editVal}
                              onChange={(e) => handleFieldChange(path, e.target.value)}
                              autoFocus={fieldToFocus === path}
                              inputRef={(input) => {
                                if (fieldToFocus === path && input) {
                                  input.focus();
                                  input.select();
                                  setFieldToFocus(null);
                                }
                              }}
                            />
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {displayVal}
                              {fv.confidence !== null && renderConfidenceChip(fv.confidence)}
                            </Typography>
                          )}
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    );
  };

  // A single KEY FIELDS scalar cell (label + editable value + confidence + Auto-Find
  // on hover). Used for SGS-specific key fields like Meter Serial and Adjustments.
  const renderKeyScalarCell = (label: string, path: string, node: any, color: string = 'primary.main') => {
    const fv = getFieldValue(node);
    const editVal = getNestedValue(path);
    const display = (fv.value === null || fv.value === undefined || fv.value === '')
      ? '—' : (typeof fv.value === 'object' ? JSON.stringify(fv.value) : fv.value);
    return (
      <Grid
        item
        xs={12}
        sm={6}
        md={3}
        key={path}
        onMouseEnter={() => setFocusedField(path)}
        onMouseLeave={() => setFocusedField(null)}
        onDoubleClick={() => { if (!isEditing) { setIsEditing(true); setFieldToFocus(path); } }}
      >
        <Box sx={{
          p: 2,
          bgcolor: focusedField === path ? 'action.hover' : 'background.paper',
          borderRadius: 1,
          border: '2px solid',
          borderColor: color,
          cursor: !isEditing ? 'pointer' : 'default',
          transition: 'background-color 0.3s',
        }}>
          <Typography variant="body2" sx={{ color }} gutterBottom><strong>{label}</strong></Typography>
          {isEditing ? (
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              value={editVal === null || editVal === undefined ? (fv.value ?? '') : editVal}
              onChange={(e) => handleFieldChange(path, e.target.value)}
              autoFocus={fieldToFocus === path}
              inputRef={(input) => { if (fieldToFocus === path && input) { input.focus(); input.select(); setFieldToFocus(null); } }}
            />
          ) : (
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {display}
              {fv.confidence !== null && renderConfidenceChip(fv.confidence)}
            </Typography>
          )}
        </Box>
      </Grid>
    );
  };

  // Recursively render nested structures (SGS: flow_rate_averages array,
  // meter_under_test / reference_meter objects) with editable leaf fields.
  const renderNested = (label: string, node: any, path: string): any => {
    const inner = (node && typeof node === 'object' && 'value' in node) ? node.value : node;

    if (Array.isArray(inner)) {
      // For flow_rate_averages, rank the flow rates so each flow is labeled HIGH /
      // LOW (matching how the meter tracks are split) with its own MF / temp /
      // pressure / correction cells shown beneath.
      const rateOf = (it: any) => {
        const r = it && typeof it === 'object'
          ? (it.flow_rate && typeof it.flow_rate === 'object' ? it.flow_rate.value : it.flow_rate)
          : undefined;
        const n = Number(r);
        return isNaN(n) ? null : n;
      };
      const rates = inner.map(rateOf);
      const valid = rates.filter((r) => r !== null) as number[];
      const minR = valid.length ? Math.min(...valid) : null;
      const maxR = valid.length ? Math.max(...valid) : null;
      return (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>{label}</Typography>
          {inner.map((item: any, i: number) => {
            const rate = rates[i];
            let fl = '';
            if (valid.length >= 2 && rate !== null) {
              if (rate === minR) fl = 'LOW';
              else if (rate === maxR) fl = 'HIGH';
            }
            const itemLabel = fl
              ? `${fl} FLOW${rate != null ? ` (${rate})` : ''}`
              : `${label.replace(/s$/, '')} ${i + 1}`;
            const barColor = fl === 'HIGH' ? 'error.light' : fl === 'LOW' ? 'info.light' : 'divider';
            return (
              <Box key={i} sx={{ pl: 1.5, mb: 1.5, borderLeft: '3px solid', borderColor: barColor }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>{itemLabel}</Typography>
                {Object.entries(item || {}).map(([k, v]: [string, any]) =>
                  <React.Fragment key={k}>
                    {renderNested(k.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()), v, `${path}.${i}.${k}`)}
                  </React.Fragment>
                )}
              </Box>
            );
          })}
        </Box>
      );
    }

    if (inner && typeof inner === 'object' && !('value' in inner) && !('confidence' in inner)) {
      return (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>{label}</Typography>
          <Box sx={{ pl: 1.5 }}>
            {Object.entries(inner).map(([k, v]: [string, any]) =>
              <React.Fragment key={k}>
                {renderNested(k.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()), v, `${path}.${k}`)}
              </React.Fragment>
            )}
          </Box>
        </Box>
      );
    }

    // Leaf: {value, confidence} scalar or a primitive
    const leafVal = (node && typeof node === 'object' && 'value' in node) ? node.value : node;
    const conf = (node && typeof node === 'object' && 'confidence' in node) ? node.confidence : null;
    return (
      <Box
        sx={{
          mb: 1,
          cursor: 'pointer',
          borderRadius: 1,
          bgcolor: focusedField === path ? 'action.hover' : 'transparent',
          transition: 'background-color 0.2s',
        }}
        onMouseEnter={() => setFocusedField(path)}
        onMouseLeave={() => setFocusedField(null)}
      >
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        {isEditing ? (
          <TextField
            fullWidth
            size="small"
            value={(() => { const nv = getNestedValue(path); return nv !== undefined && nv !== null ? nv : (leafVal ?? ''); })()}
            onChange={(e) => handleFieldChange(path, e.target.value)}
          />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body1">{(leafVal !== undefined && leafVal !== null && leafVal !== '') ? String(leafVal) : '-'}</Typography>
            {conf !== null && (
              <Chip
                label={`${Math.round(Number(conf) * 100)}%`}
                size="small"
                color={Number(conf) > 0.8 ? 'success' : Number(conf) > 0.5 ? 'warning' : 'error'}
                variant="outlined"
              />
            )}
          </Box>
        )}
      </Box>
    );
  };

  const renderFieldWithConfidence = (label: string, field: any, fieldKey?: string) => {
    // Nested structures (arrays like flow_rate_averages, objects like
    // meter_under_test / reference_meter) are not {value, confidence} scalars --
    // render them recursively so they display + edit instead of crashing React.
    const innerVal = (field && typeof field === 'object' && 'value' in field) ? field.value : field;
    if (Array.isArray(innerVal) || (innerVal && typeof innerVal === 'object')) {
      return renderNested(label, field, fieldKey || label);
    }

    const { value, confidence } = getFieldValue(field);
    const isFocused = focusedField === fieldKey;

    // Check if value is a JSON string that needs parsing (for nested objects)
    let displayValue = value;
    if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
      try {
        const parsed = JSON.parse(value);
        // If it's an object with nested fields, render them individually
        if (typeof parsed === 'object' && !Array.isArray(parsed)) {
          return (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {label}
              </Typography>
              <Box sx={{ pl: 2 }}>
                {Object.entries(parsed).map(([subKey, subValue]: [string, any]) => {
                  const subFieldValue = typeof subValue === 'object' && 'value' in subValue
                    ? subValue.value
                    : subValue;
                  const subConfidence = typeof subValue === 'object' && 'confidence' in subValue
                    ? subValue.confidence
                    : null;

                  return (
                    <Box key={subKey} sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {subKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </Typography>
                      {isEditing && fieldKey ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={
                            // Try to get the edited value first
                            (() => {
                              if (fieldKey && editedData[fieldKey]) {
                                if (typeof editedData[fieldKey] === 'object' && !Array.isArray(editedData[fieldKey])) {
                                  if (editedData[fieldKey][subKey]?.value !== undefined) {
                                    return editedData[fieldKey][subKey].value;
                                  } else if (editedData[fieldKey][subKey] !== undefined) {
                                    return editedData[fieldKey][subKey];
                                  }
                                }
                              }
                              return subFieldValue || '';
                            })()
                          }
                          onChange={(e) => {
                            if (!fieldKey) return;

                            const newData = JSON.parse(JSON.stringify(editedData));

                            // Initialize the field if it doesn't exist
                            if (!newData[fieldKey]) {
                              newData[fieldKey] = {};
                            }

                            // If it's still a string, parse it
                            if (typeof newData[fieldKey] === 'string') {
                              try {
                                newData[fieldKey] = JSON.parse(newData[fieldKey]);
                              } catch {
                                newData[fieldKey] = {};
                              }
                            }

                            // Store in embedded format
                            newData[fieldKey][subKey] = {
                              value: e.target.value,
                              confidence: 1.0
                            };

                            setEditedData(newData);
                          }}
                        />
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1">
                            {cleanOCRErrors(subFieldValue, subKey)}
                          </Typography>
                          {subConfidence !== null && (
                            <Chip
                              label={`${Math.round(subConfidence * 100)}%`}
                              size="small"
                              color={subConfidence >= 0.9 ? 'success' : subConfidence >= 0.7 ? 'warning' : 'error'}
                              variant="outlined"
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        }
        displayValue = JSON.stringify(parsed, null, 2);
      } catch (e) {
        // If parsing fails, use the original value
        displayValue = cleanOCRErrors(value, fieldKey);
      }
    } else {
      // Apply OCR corrections for display
      displayValue = cleanOCRErrors(value, fieldKey);
    }

    return (
      <Box
        sx={{
          mb: 1,
          p: 1,
          backgroundColor: isFocused ? 'action.hover' : 'transparent',
          borderRadius: 1,
          transition: 'background-color 0.3s',
          cursor: !isEditing ? 'pointer' : 'default'
        }}
        onMouseEnter={() => fieldKey && setFocusedField(fieldKey)}
        onMouseLeave={() => setFocusedField(null)}
        onDoubleClick={() => {
          if (!isEditing && fieldKey) {
            setIsEditing(true);
            setFieldToFocus(fieldKey);
          }
        }}
      >
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {label}
        </Typography>
        {isEditing && fieldKey ? (
          <TextField
            fullWidth
            size="small"
            value={editedData[fieldKey]?.value !== undefined ? editedData[fieldKey].value : editedData[fieldKey] || ''}
            onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
            onFocus={() => setFocusedField(fieldKey)}
            onBlur={() => setFocusedField(null)}
            autoFocus={fieldToFocus === fieldKey}
            inputRef={(input) => {
              if (fieldToFocus === fieldKey && input) {
                input.focus();
                setFieldToFocus(null);
              }
            }}
          />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body1">
              {displayValue || '-'}
              {displayValue !== value && value && (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  (corrected)
                </Typography>
              )}
            </Typography>
            {confidence !== null && (
              <Chip
                label={`${(confidence * 100).toFixed(0)}%`}
                size="small"
                color={confidence > 0.8 ? 'success' : confidence > 0.5 ? 'warning' : 'error'}
                variant="outlined"
              />
            )}
          </Box>
        )}
      </Box>
    );
  };

  // Handle field changes during editing - properly handle nested structures
  const handleFieldChange = (fieldPath: string, value: any) => {
    console.log(`handleFieldChange called for ${fieldPath} with value:`, value, `type: ${typeof value}`);
    const newData = JSON.parse(JSON.stringify(editedData)); // Deep clone to avoid mutation issues
    const parts = fieldPath.split('.');

    // Navigate to the correct nested location
    let current = newData;
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];

      // Ensure the path exists
      if (!current[part]) {
        current[part] = {};
      }

      // Handle embedded format {value: {...}, confidence: ...}
      if (current[part] && typeof current[part] === 'object' && 'value' in current[part]) {
        if (!current[part].value || typeof current[part].value !== 'object') {
          current[part].value = {};
        }
        current = current[part].value;
      } else {
        current = current[part];
      }
    }

    // Update the final field
    const lastKey = parts[parts.length - 1];

    // Always store in embedded format for consistency
    if (current[lastKey] && typeof current[lastKey] === 'object' && 'value' in current[lastKey]) {
      current[lastKey] = { ...current[lastKey], value, confidence: 1.0 };
    } else {
      current[lastKey] = { value, confidence: 1.0 };
    }

    console.log(`After setting ${fieldPath}, value in newData:`, current[lastKey]);
    setEditedData(newData);
  };

  // Handle test run field changes
  const handleTestRunChange = (runIndex: number, fieldName: string, value: any) => {
    const newData = JSON.parse(JSON.stringify(editedData));

    // Ensure test_runs structure exists
    if (!newData.test_runs) {
      newData.test_runs = { value: [], confidence: 1.0 };
    }
    if (!newData.test_runs.value) {
      newData.test_runs.value = [];
    }

    // Ensure the run exists
    while (newData.test_runs.value.length <= runIndex) {
      newData.test_runs.value.push({});
    }

    // Update the field in embedded format
    const run = newData.test_runs.value[runIndex];
    if (run[fieldName] && typeof run[fieldName] === 'object' && 'value' in run[fieldName]) {
      run[fieldName] = { ...run[fieldName], value, confidence: 1.0 };
    } else {
      run[fieldName] = { value, confidence: 1.0 };
    }

    setEditedData(newData);
  };

  // Add a new field to the data structure
  const handleAddField = (section: string, fieldName: string, initialValue: any = '') => {
    const newData = JSON.parse(JSON.stringify(editedData));

    if (section === 'root') {
      newData[fieldName] = { value: initialValue, confidence: 1.0 };
    } else {
      // For nested sections like averages, corrections, final_results
      if (!newData[section]) {
        newData[section] = { value: {}, confidence: 1.0 };
      }
      if (!newData[section].value) {
        newData[section].value = {};
      }
      newData[section].value[fieldName] = { value: initialValue, confidence: 1.0 };
    }

    setEditedData(newData);
  };

  // Save edited data
  const markAsVerified = async (goNext = false) => {
    setLoading(true);
    try {
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;

      // Create a copy of the data with all confidence scores set to 100%
      const verifiedData = JSON.parse(JSON.stringify(localExtractedData));

      // Set all confidence scores to 100%
      for (const field in verifiedData) {
        const fieldData = verifiedData[field];
        if (typeof fieldData === 'object' && fieldData !== null && 'confidence' in fieldData) {
          fieldData.confidence = 1.0;
        }
      }

      // Use same pattern as saveEditedData - send full reportData with modifications
      const updatePayload = {
        ...reportData,
        extracted_data: verifiedData,
        user_validated: true,
        overall_confidence: 1.0,
        validation_timestamp: new Date().toISOString()
      };

      console.log('Marking as verified with payload:', {
        report_id: reportData.report_id,
        ingestion_timestamp: reportData.ingestion_timestamp,
        has_extracted_data: !!verifiedData
      });

      const response = await axios.put(
        `${API_ENDPOINT}/report/${encodeURIComponent(reportData.report_id)}${typeQuery}`,
        updatePayload,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`
          }
        }
      );

      console.log('Mark verified response:', response.status, response.data);

      if (response.status !== 200) {
        throw new Error(`Failed to mark as verified: ${response.status}`);
      }

      const updatedReport = response.data;

      // Show success message
      setSaveStatus({
        show: true,
        message: 'Document marked as verified',
        severity: 'success'
      });

      // Call onSave callback to update the queue with proper structure
      if (onSave) {
        // Make sure the structure matches what onSave expects
        const reportForQueue = {
          ...updatedReport,
          report_id: reportData.report_id,
          extracted_data: verifiedData,
          user_validated: true,
          overall_confidence: 1.0
        };
        onSave(reportForQueue);
      }

      // Advance to the next unverified report, or return to the queue.
      setTimeout(() => {
        if (goNext && onSaveAndNext) {
          onSaveAndNext();
        } else if (onClose) {
          onClose();
        }
      }, 500);

    } catch (error) {
      console.error('Failed to mark as verified:', error);
      setSaveStatus({
        show: true,
        message: 'Failed to mark as verified',
        severity: 'error'
      });
    }
    setLoading(false);
  };

  const saveEditedData = async (goNext = false) => {
    setLoading(true);
    try {
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;

      // Track corrections for ML + accuracy analytics. Walk BOTH top-level and
      // nested SGS fields (flow_rate_averages.*, meter_under_test.*, reference_meter.*)
      // to the leaf level and record ONLY real value changes. The previous version
      // only compared top-level fields and logged phantom diffs on nested objects
      // (JSON quote-style mismatches), which would corrupt the accuracy numbers.
      const corrections: any[] = [];
      const originalData: any = localExtractedData || {};
      // metadata keys that live INSIDE a field node -- never a user-edited value
      const NODE_META = new Set(['confidence', 'source_text', 'bounding_box', 'textract_confidence', 'page']);
      // top-level keys that are system metadata, not extracted fields
      const TOP_META = new Set([
        'overall_confidence', 'has_bounding_boxes', 'bounding_boxes', 'textract_corrections',
        'data_version', 'requires_human_review', 'missing_required_fields', 'fields_below_threshold',
        'processing_type', 'source_page', 'source_document', 'source_image',
      ]);
      const leafConf = (n: any) => (n && typeof n === 'object' && 'confidence' in n) ? n.confidence : null;
      const scalar = (v: any) => (v === null || v === undefined) ? '' : (typeof v === 'object' ? '' : String(v));
      const walkDiff = (orig: any, edit: any, path: string) => {
        const oInner = (orig && typeof orig === 'object' && 'value' in orig) ? orig.value : orig;
        const eInner = (edit && typeof edit === 'object' && 'value' in edit) ? edit.value : edit;
        // Leaf scalar
        if (eInner === null || eInner === undefined || typeof eInner !== 'object') {
          const ev = scalar(eInner);
          const ov = scalar(oInner);
          if (ev !== ov && (ev !== '' || ov !== '')) {
            let cb = leafConf(orig);
            if (cb === null || cb === undefined || isNaN(Number(cb))) cb = 0;
            corrections.push({ field_name: path, original_value: ov, corrected_value: ev, confidence_before: Number(cb) });
          }
          return;
        }
        // Array (flow_rate_averages, test_runs) -- diff item by item
        if (Array.isArray(eInner)) {
          eInner.forEach((item, i) => walkDiff(Array.isArray(oInner) ? oInner[i] : undefined, item, `${path}.${i}`));
          return;
        }
        // Object (meter_under_test, reference_meter, etc.) -- recurse into sub-fields
        Object.keys(eInner).forEach((k) => {
          if (NODE_META.has(k)) return;
          walkDiff(oInner && typeof oInner === 'object' ? oInner[k] : undefined, eInner[k], path ? `${path}.${k}` : k);
        });
      };
      Object.keys(editedData || {}).forEach((field) => {
        if (TOP_META.has(field)) return;
        walkDiff(originalData[field], (editedData as any)[field], field);
      });

      // Track corrections if any changes were made
      if (corrections.length > 0) {
        try {
          await axios.post(`${API_ENDPOINT}/corrections`, {
            report_id: reportData.report_id,
            corrections: corrections
          }, {
            headers: {
              'Authorization': `Bearer ${idToken}`
            }
          });
        } catch (corrError) {
          console.error('Failed to track corrections:', corrError);
        }
      }

      // Save the edited data - ensure we're sending clean data
      // Deep clean the editedData to ensure proper structure
      const cleanedData = JSON.parse(JSON.stringify(editedData));

      // Ensure meter_factor is in the correct format if it exists
      if (cleanedData.meter_factor) {
        // If it has extra fields like source_text or bounding_box, keep only value and confidence
        if (typeof cleanedData.meter_factor === 'object' && 'value' in cleanedData.meter_factor) {
          cleanedData.meter_factor = {
            value: cleanedData.meter_factor.value,
            confidence: 1.0
          };
        }
      }

      // Do the same for averages.avg_meter_factor if it exists
      if (cleanedData.averages?.avg_meter_factor) {
        if (typeof cleanedData.averages.avg_meter_factor === 'object' && 'value' in cleanedData.averages.avg_meter_factor) {
          cleanedData.averages.avg_meter_factor = {
            value: cleanedData.averages.avg_meter_factor.value,
            confidence: 1.0
          };
        }
      }

      // Send the entire object with updated extracted_data
      const updatePayload = {
        ...reportData,  // Include ALL fields from the original report
        extracted_data: cleanedData,  // Override with cleaned data
        user_validated: true,
        validation_timestamp: new Date().toISOString()
      };

      console.log('Saving with payload:', {
        report_id: reportData.report_id,
        ingestion_timestamp: reportData.ingestion_timestamp,
        meter_id_value: editedData?.meter_id?.value || editedData?.meter_id,
        meter_id_full: editedData?.meter_id,
        meter_factor_value: editedData?.meter_factor?.value || editedData?.meter_factor,
        meter_factor_full: editedData?.meter_factor,
        avg_meter_factor: (editedData?.averages as any)?.avg_meter_factor,
        extracted_data_keys: Object.keys(editedData || {})
      });
      console.log('Full editedData structure:', editedData);

      console.log('Calling update endpoint with payload:', updatePayload);
      const response = await axios.put(
        `${API_ENDPOINT}/report/${encodeURIComponent(reportData.report_id)}${typeQuery}`,
        updatePayload,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`
          }
        }
      );

      console.log('Save response:', response.status, response.data);
      console.log('Response extracted_data:', response.data?.report?.extracted_data);
      console.log('Response meter_factor:', response.data?.report?.extracted_data?.meter_factor);

      if (response.status === 200) {
        setSaveStatus({
          show: true,
          message: 'Changes saved successfully!',
          severity: 'success'
        });

        // Use the response data as the source of truth after save
        const savedExtractedData = response.data?.report?.extracted_data || editedData;

        console.log('Response meter_factor value:', savedExtractedData?.meter_factor?.value);
        console.log('Response meter_factor type:', typeof savedExtractedData?.meter_factor?.value);
        console.log('Original edited meter_factor:', editedData?.meter_factor);

        // Update local extracted data to reflect the saved changes
        setLocalExtractedData(savedExtractedData);
        // Also update editedData to match what was saved
        setEditedData(savedExtractedData);

        setIsEditing(false);
        setJustSaved(true); // Set flag to prevent data reset from parent prop update
        console.log('Save successful, updated local data with:', savedExtractedData);
        console.log('Meter factor after save:', savedExtractedData?.meter_factor);

        // Notify parent component with the updated data
        if (onSave && response.data?.report) {
          console.log('Calling parent onSave with:', response.data.report);
          onSave(response.data.report);
        }

        // Advance to the next unverified report if requested (Save & Next).
        if (goNext && onSaveAndNext) {
          setTimeout(() => onSaveAndNext(), 300);
        }
      }
    } catch (error) {
      console.error('Error saving data:', error);
      setSaveStatus({
        show: true,
        message: 'Failed to save changes. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Check if this is from a multipage document (moved outside for reuse)
  const isMultipage = reportData.is_multipage ||
                      localExtractedData?.is_multipage ||
                      (reportData.source_document && reportData.source_page);

  // Get document display component based on file type
  const getDocumentDisplay = () => {
    // Any page-image doc (crude ticket OR single-doc proving): render the current page
    // PNG with the Auto-Find overlay + page nav. Falls through to the normal path for
    // docs without page images (older tickets, multi-mode provings, non-PDFs).
    if (docPageKeys.length > 0) {
      return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1 }}>
            <IconButton size="small" onClick={() => setDocPage(p => Math.max(1, p - 1))} disabled={docPage <= 1}>
              <NavigateBeforeIcon />
            </IconButton>
            <Typography variant="body2" sx={{ minWidth: 90, textAlign: 'center' }}>
              Page {docPage} / {docTotalPages}
            </Typography>
            <IconButton size="small" onClick={() => setDocPage(p => Math.min(docTotalPages, p + 1))} disabled={docPage >= docTotalPages}>
              <NavigateNextIcon />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            {docPageUrl ? (
              <DocumentWithHighlights
                imageUrl={docPageUrl}
                extractedData={localExtractedData as any || {}}
                focusedField={focusedField}
                onFieldClick={(fieldName) => {
                  setFocusedField(fieldName);
                  const element = document.getElementById(`field-${fieldName}`);
                  element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }}
                currentPage={docPage}
              />
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
                <CircularProgress />
              </Box>
            )}
          </Box>
        </Box>
      );
    }

    if (imageLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!imageUrl) {
      return (
        <Box sx={{ textAlign: 'center', p: 4 }}>
          <Typography color="text.secondary" gutterBottom>
            Document not available
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchImageUrl}
            sx={{ mt: 2 }}
          >
            Retry Loading
          </Button>
        </Box>
      );
    }

    // Detect file type and processing type
    const fileType = reportData.file_type || '';
    const filename = reportData.original_filename || reportData.s3_key || '';
    const pageNumber = reportData.page_number || reportData.source_page_number;
    const processingType = reportData.processing_type || '';
    const hasBoundingBoxes = reportData.has_bounding_boxes ||
                             extractedData?.has_bounding_boxes ||
                             processingType === 'hybrid_textract_image';

    // Check if it's a page-specific image from hybrid processing or Textract with bounding boxes
    // Can be in /images/, /pages/, or /processed/ folder
    const isPageImage = ((imageUrl.includes('/images/') || imageUrl.includes('/pages/') || imageUrl.includes('/processed/')) &&
                         imageUrl.includes('_page_')) ||
                        processingType === 'textract_with_bounding_boxes';

    // Debug logging
    console.log('Document detection:', {
      fileType,
      filename,
      pageNumber,
      processingType,
      hasBoundingBoxes,
      isPageImage,
      isMultipage,
      sourceDocument: reportData.source_document,
      sourcePage: reportData.source_page,
      imageUrl
    });

    // If we have bounding boxes and it's an appropriate image type, use DocumentWithHighlights
    if (hasBoundingBoxes && (isPageImage || processingType === 'textract_with_bounding_boxes')) {
      console.log('Extracted data for DocumentWithHighlights:', localExtractedData);

      // Log a few fields to see their structure
      if (localExtractedData) {
        ['meter_id', 'test_date', 'meter_factor'].forEach(field => {
          const fieldData = (localExtractedData as any)[field];
          if (fieldData) {
            console.log(`Field ${field}:`, fieldData);
          }
        });
      }

      return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <DocumentWithHighlights
            imageUrl={imageUrl}
            extractedData={localExtractedData as any || {}}
            focusedField={focusedField}
            onFieldClick={(fieldName) => {
              setFocusedField(fieldName);
              // Scroll to the field in the data panel
              const element = document.getElementById(`field-${fieldName}`);
              element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }}
            currentPage={1}  // Page images are always single page
          />
        </Box>
      );
    }

    // Regular image display (for images without bounding boxes)
    if (isPageImage || fileType.includes('image') || fileType.includes('png') || fileType.includes('jpg')) {
      return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Zoom and Rotation Controls */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 1 }}>
            <ButtonGroup size="small" variant="outlined">
              <IconButton
                onClick={() => setZoomLevel(Math.max(0.05, zoomLevel - 0.25))}
                disabled={zoomLevel <= 0.05}
                size="small"
                title="Zoom Out"
              >
                <ZoomOutIcon />
              </IconButton>
              <Button
                onClick={() => setZoomLevel(1)}
                size="small"
                sx={{ minWidth: '80px' }}
              >
                {Math.round(zoomLevel * 100)}%
              </Button>
              <IconButton
                onClick={() => setZoomLevel(Math.min(3, zoomLevel + 0.25))}
                disabled={zoomLevel >= 3}
                size="small"
                title="Zoom In"
              >
                <ZoomInIcon />
              </IconButton>
              <IconButton
                onClick={() => {
                  // Reset to 50% zoom and center
                  setZoomLevel(0.5);

                  // Center the viewport after zoom change
                  setTimeout(() => {
                    const container = document.getElementById('image-container');
                    if (container) {
                      const centerX = (container.scrollWidth - container.clientWidth) / 2;
                      const centerY = (container.scrollHeight - container.clientHeight) / 2;
                      container.scrollLeft = centerX;
                      container.scrollTop = centerY;
                    }
                  }, 50);
                }}
                size="small"
                title="Reset to 50%"
              >
                <FitScreenIcon />
              </IconButton>
            </ButtonGroup>

            {/* Rotation Controls */}
            <ButtonGroup size="small" variant="outlined">
              <IconButton
                onClick={() => setRotation(rotation - 90)}
                size="small"
                title="Rotate Left"
                sx={{ transform: 'scaleX(-1)' }}
              >
                <RotateRightIcon />
              </IconButton>
              <IconButton
                onClick={() => setRotation(rotation + 90)}
                size="small"
                title="Rotate Right"
              >
                <RotateRightIcon />
              </IconButton>
            </ButtonGroup>
          </Box>

          {/* Image Container */}
          <Box
            id="image-container"
            sx={{
              overflow: 'auto',
              flex: 1,
              backgroundColor: 'rgba(0,0,0,0.02)',
              borderRadius: 1,
              position: 'relative',
              cursor: isDragging ? 'grabbing' : 'grab',
            }}
            onWheel={(e) => {
              // Zoom on Ctrl+Scroll or Cmd+Scroll
              if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                const delta = e.deltaY > 0 ? -0.1 : 0.1;
                setZoomLevel(prev => Math.min(Math.max(prev + delta, 0.05), 3));
              }
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              setIsDragging(true);
              setDragStart({ x: e.clientX, y: e.clientY });
              const container = document.getElementById('image-container');
              if (container) {
                setScrollStart({ x: container.scrollLeft, y: container.scrollTop });
              }
            }}
            onMouseMove={(e) => {
              if (isDragging) {
                e.preventDefault();
                const container = document.getElementById('image-container');
                if (container) {
                  const deltaX = dragStart.x - e.clientX;
                  const deltaY = dragStart.y - e.clientY;
                  container.scrollLeft = scrollStart.x + deltaX;
                  container.scrollTop = scrollStart.y + deltaY;
                }
              }
            }}
            onMouseUp={() => {
              setIsDragging(false);
            }}
            onMouseLeave={() => {
              setIsDragging(false);
            }}
          >
            <div style={{
              padding: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
              <img
                src={imageUrl}
                alt="Source Document"
                style={{
                  transform: `scale(${zoomLevel})`,
                  transformOrigin: 'center',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  transition: 'transform 0.1s',
                }}
                draggable={false}
                onError={() => {
                  console.error('Image failed to load');
                  window.open(imageUrl, '_blank');
                }}
                onLoad={() => {
                  // Center the viewport after image loads
                  if (!isInitialized) {
                    const container = document.getElementById('image-container');
                    if (container) {
                      // Calculate center position
                      const centerX = (container.scrollWidth - container.clientWidth) / 2;
                      const centerY = (container.scrollHeight - container.clientHeight) / 2;
                      container.scrollLeft = centerX;
                      container.scrollTop = centerY;
                      setIsInitialized(true);
                    }
                  }
                }}
              />
            </div>
          </Box>

          {isPageImage && !hasBoundingBoxes && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Page {pageNumber} - Processed without coordinate extraction
            </Alert>
          )}
        </Box>
      );
    }

    // Check if it's a PDF based on multiple indicators
    const isPDF = fileType.includes('pdf') ||
                   filename.toLowerCase().includes('.pdf') ||
                   reportData.s3_key?.toLowerCase().includes('.pdf');

    if (isPDF && !isPageImage) {
      // For PDFs, we need to use proper page navigation
      // PDF.js uses 1-based page numbers, and #page= parameter
      const pdfUrl = pageNumber ? `${imageUrl}#page=${pageNumber}` : imageUrl;

      return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {pageNumber && (
            <Alert severity="info" sx={{ mb: 1 }}>
              Viewing Page {pageNumber} of multi-page PDF
            </Alert>
          )}
          <Box sx={{ flex: 1, position: 'relative' }}>
            <iframe
              src={pdfUrl}
              width="100%"
              height="650px"
              style={{ border: 'none' }}
              title={`PDF Document - Page ${pageNumber || 'All'}`}
            />
          </Box>
          <Box sx={{ mt: 1, display: 'flex', gap: 1, justifyContent: 'center' }}>
            <Button
              size="small"
              startIcon={<OpenInNewIcon />}
              onClick={() => window.open(pdfUrl, '_blank')}
            >
              Open Page {pageNumber || 'All'} in New Tab
            </Button>
          </Box>
        </Box>
      );
    }

    if (fileType.includes('xls')) {
      return (
        <Box sx={{ textAlign: 'center', p: 4 }}>
          <Typography variant="body1" gutterBottom>
            Excel file - Cannot display inline
          </Typography>
          <Button
            variant="contained"
            startIcon={<OpenInNewIcon />}
            onClick={() => window.open(imageUrl, '_blank')}
          >
            Download/View
          </Button>
        </Box>
      );
    }

    // Default to image display with zoom and pan
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Zoom Controls */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 1 }}>
          <ButtonGroup size="small" variant="outlined">
            <IconButton
              onClick={() => setZoomLevel(Math.max(0.05, zoomLevel - 0.25))}
              disabled={zoomLevel <= 0.05}
              size="small"
              title="Zoom Out"
            >
              <ZoomOutIcon />
            </IconButton>
            <Button
              onClick={() => setZoomLevel(1)}
              size="small"
              sx={{ minWidth: '80px' }}
            >
              {Math.round(zoomLevel * 100)}%
            </Button>
            <IconButton
              onClick={() => setZoomLevel(Math.min(3, zoomLevel + 0.25))}
              disabled={zoomLevel >= 3}
              size="small"
              title="Zoom In"
            >
              <ZoomInIcon />
            </IconButton>
            <IconButton
              onClick={() => {
                // Reset to 50% zoom and center
                setZoomLevel(0.5);

                // Center the viewport after zoom change
                setTimeout(() => {
                  const container = document.getElementById('image-container');
                  if (container) {
                    const centerX = (container.scrollWidth - container.clientWidth) / 2;
                    const centerY = (container.scrollHeight - container.clientHeight) / 2;
                    container.scrollLeft = centerX;
                    container.scrollTop = centerY;
                  }
                }, 50);
              }}
              size="small"
              title="Reset to 50%"
            >
              <FitScreenIcon />
            </IconButton>
          </ButtonGroup>
        </Box>

        {/* Image Container */}
        <Box
          id="image-container"
          sx={{
            overflow: 'auto',
            flex: 1,
            backgroundColor: 'rgba(0,0,0,0.02)',
            borderRadius: 1,
            position: 'relative',
            cursor: isDragging ? 'grabbing' : 'grab',
          }}
          onWheel={(e) => {
            // Zoom on Ctrl+Scroll or Cmd+Scroll
            if (e.ctrlKey || e.metaKey) {
              e.preventDefault();
              const delta = e.deltaY > 0 ? -0.1 : 0.1;
              setZoomLevel(prev => Math.min(Math.max(prev + delta, 0.05), 3));
            }
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            setIsDragging(true);
            setDragStart({ x: e.clientX, y: e.clientY });
            const container = document.getElementById('image-container');
            if (container) {
              setScrollStart({ x: container.scrollLeft, y: container.scrollTop });
            }
          }}
          onMouseMove={(e) => {
            if (isDragging) {
              e.preventDefault();
              const container = document.getElementById('image-container');
              if (container) {
                const deltaX = dragStart.x - e.clientX;
                const deltaY = dragStart.y - e.clientY;
                container.scrollLeft = scrollStart.x + deltaX;
                container.scrollTop = scrollStart.y + deltaY;
              }
            }
          }}
          onMouseUp={() => {
            setIsDragging(false);
          }}
          onMouseLeave={() => {
            setIsDragging(false);
          }}
        >
          <div style={{
            padding: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
            <img
              src={imageUrl}
              alt="Source Document"
              style={{
                transform: `scale(${zoomLevel}) rotate(${rotation}deg)`,
                transformOrigin: 'center',
                width: 'auto',
                height: 'auto',
                border: focusedField ? '2px solid #1976d2' : 'none',
                transition: 'transform 0.1s',
                objectFit: 'contain'
              }}
            draggable={false}
            onError={() => {
              console.error('Image failed to load');
              window.open(imageUrl, '_blank');
            }}
            onLoad={() => {
              // Center the viewport after image loads
              if (!isInitialized) {
                const container = document.getElementById('image-container');
                if (container) {
                  // Calculate center position
                  const centerX = (container.scrollWidth - container.clientWidth) / 2;
                  const centerY = (container.scrollHeight - container.clientHeight) / 2;
                  container.scrollLeft = centerX;
                  container.scrollTop = centerY;
                  setIsInitialized(true);
                }
              }
            }}
            />
          </div>
        </Box>
    </Box>
    );
  };

  // Extract data fields
  const testRunsField = getFieldValue(localExtractedData?.test_runs);
  const testRuns = testRunsField.value || [];

  const finalResultsField = getFieldValue(localExtractedData?.final_results);
  const finalResults: any = finalResultsField.value || {};

  // Crude-ticket field panel: iterate the ticket schema generically and reuse the
  // existing renderFieldWithConfidence (which handles {value,confidence} leaves,
  // nested objects, inline edit, and Auto-Find focus). No proving-specific layout.
  const renderTicketFields = () => {
    const data: any = localExtractedData || {};
    const skip = new Set(['document_type']);
    const prettify = (k: string) => k.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
    // Show the KEY fields first, then everything else in the order captured.
    const keyOrder = ['report_no', 'discharge_date', 'product', 'vessel', 'net_outturn', 'key_quality'];
    const allKeys = Object.keys(data).filter(k => !skip.has(k));
    const orderedKeys = [
      ...keyOrder.filter(k => allKeys.includes(k)),
      ...allKeys.filter(k => !keyOrder.includes(k)),
    ];
    return (
      <Card
        sx={{ mb: 3, borderColor: 'secondary.main', borderWidth: 2, borderStyle: 'solid', backgroundColor: '#f3e5f5' }}
        elevation={3}
      >
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ color: 'secondary.main', fontWeight: 'bold', mb: 1 }}>
            🚢 CRUDE TICKET — SGS Key Data Report (Discharge)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            KEY fields feed the Mass Balance Utility: <strong>Net Outturn</strong> (m³ @15°C + Metric Tons)
            and the <strong>Vessel's Composite</strong> quality (density, KF water, sediment, S&amp;W).
            Click any field to locate it on the document.
          </Typography>
          {orderedKeys.map((key) => {
            const isKey = keyOrder.includes(key);
            return (
              <Box
                key={key}
                sx={{
                  mb: 1.5,
                  ...(isKey ? { p: 1, borderLeft: '3px solid', borderColor: 'secondary.main', borderRadius: 1 } : {}),
                }}
              >
                {renderFieldWithConfidence(prettify(key), data[key], key)}
              </Box>
            );
          })}
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      <Grid container spacing={2} sx={{ height: 'calc(100vh - 100px)', overflow: 'hidden' }}>
        {/* Left Panel - Document Image */}
        <Grid item xs={12} md={6} sx={{ height: '100%', overflow: 'auto' }}>
          <Paper
            elevation={3}
            sx={{
              p: 2,
              position: 'sticky',
              top: 0,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              zIndex: 10
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6">
                  Source Document
                  {isMultipage && (
                    <Chip
                      label="Multipage"
                      size="small"
                      color="info"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Typography>
                {(reportData.page_number || reportData.source_page_number) && (
                  <Typography variant="subtitle2" color="primary">
                    Page {reportData.page_number || reportData.source_page_number}
                    {reportData.total_pages && reportData.total_pages > 1 && ` of ${reportData.total_pages}`}
                    {reportData.source_document && (
                      <span style={{ display: 'block', fontSize: '0.85em', color: '#666' }}>
                        From: {reportData.source_document.split('/').pop()}
                      </span>
                    )}
                  </Typography>
                )}
              </Box>
              {onClose && (
                <Button size="small" onClick={onClose}>
                  Close
                </Button>
              )}
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {getDocumentDisplay()}
            </Box>
          </Paper>
        </Grid>

        {/* Right Panel - Extracted Data */}
        <Grid item xs={12} md={6} sx={{ height: '100%', overflow: 'auto' }}>
          <Paper elevation={3} sx={{ p: 2, height: '100%', overflow: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Extracted Data</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {!isEditing ? (
                  <>
                    <Button
                      variant="contained"
                      startIcon={<EditIcon />}
                      onClick={() => setIsEditing(true)}
                      size="small"
                    >
                      Edit Data
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={loading ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                      onClick={() => markAsVerified(false)}
                      disabled={loading}
                      size="small"
                    >
                      Mark Verified
                    </Button>
                    {onSaveAndNext && remainingCount > 0 && (
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={loading ? <CircularProgress size={16} /> : <SkipNextIcon />}
                        onClick={() => markAsVerified(true)}
                        disabled={loading}
                        size="small"
                      >
                        Verify & Next ({remainingCount} left)
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => setAddFieldDialog(true)}
                      size="small"
                    >
                      Add Field
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
                      onClick={() => saveEditedData(false)}
                      disabled={loading}
                      size="small"
                    >
                      Save
                    </Button>
                    {onSaveAndNext && remainingCount > 0 && (
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={loading ? <CircularProgress size={16} /> : <SkipNextIcon />}
                        onClick={() => saveEditedData(true)}
                        disabled={loading}
                        size="small"
                      >
                        Save & Next ({remainingCount} left)
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => setAddFieldDialog(true)}
                      disabled={loading}
                      size="small"
                    >
                      Add Field
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={() => {
                        setIsEditing(false);
                        setEditedData(localExtractedData || {});
                      }}
                      disabled={loading}
                      size="small"
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {isTicket ? renderTicketFields() : (
            <>
            {/* KEY FIELDS FOR METER HISTORY - Most Important Section */}
            <Card
              sx={{
                mb: 3,
                borderColor: 'error.main',
                borderWidth: 2,
                borderStyle: 'solid',
                backgroundColor: '#fff3e0'
              }}
              elevation={3}
            >
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ color: 'error.main', fontWeight: 'bold', mb: 2 }}>
                  🔑 KEY FIELDS FOR METER HISTORY & REGRESSION ANALYSIS
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  CRITICAL: These fields determine meter tracking, control charts, and regression predictions. Verify carefully!
                  <br />
                  <strong>Red Border</strong> = Required for tracking | <strong>Blue Border</strong> = Required for regression | <strong>Light Blue</strong> = Optional enhancement
                </Typography>

                {/* Format detection and display */}
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Detected Format:</Typography>
                  {(() => {
                    const averagesField = getFieldValue(localExtractedData?.averages);
                    const averages = averagesField.value || localExtractedData?.averages;
                    // report_format is the AI-classified template tag in the current
                    // schema (SGS | PMEC | DEFAULT); fall back to the legacy `format`
                    // field. Structural signals below are a backstop if the tag is missing.
                    const reportFormat = (
                      getFieldValue((localExtractedData as any)?.report_format).value ||
                      getFieldValue((localExtractedData as any)?.format).value || ''
                    ).toString().toUpperCase();
                    // flow_rate_averages is TOP-LEVEL in the current SGS schema, but was
                    // nested under `averages` in the legacy shape — check both.
                    const rawFlow = (localExtractedData as any)?.flow_rate_averages;
                    const topFlow = Array.isArray(rawFlow) ? rawFlow : rawFlow?.value;
                    const nestedFlow = averages?.flow_rate_averages;
                    const hasFlowRateAverages =
                      (Array.isArray(topFlow) && topFlow.length > 0) ||
                      (Array.isArray(nestedFlow) && nestedFlow.length > 0) ||
                      (!!nestedFlow && typeof nestedFlow === 'object');
                    const hasAvgMeterFactor = averages?.avg_meter_factor;

                    if (reportFormat === 'SGS' || hasFlowRateAverages) {
                      return (
                        <>
                          <Chip label="SGS Multi-Flow Format" size="small" color="error" />
                          <Typography variant="caption" color="text.secondary">
                            ⚠️ Each flow_rate_average creates a separate control chart!
                          </Typography>
                        </>
                      );
                    } else if (reportFormat === 'PMEC' || hasAvgMeterFactor) {
                      return (
                        <>
                          <Chip label="PMEC Format" size="small" color="warning" />
                          <Typography variant="caption" color="text.secondary">
                            Uses averages → avg_meter_factor (overrides standard meter_factor)
                          </Typography>
                        </>
                      );
                    } else {
                      return (
                        <>
                          <Chip label="Standard Format" size="small" color="success" />
                          <Typography variant="caption" color="text.secondary">
                            Uses standard meter_factor field directly
                          </Typography>
                        </>
                      );
                    }
                  })()}
                </Box>

                <Grid container spacing={2}>
                  {/* Meter ID - CRITICAL */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{
                      p: 2,
                      bgcolor: focusedField === 'meter_id' ? 'action.hover' : 'background.paper',
                      borderRadius: 1,
                      border: '2px solid',
                      borderColor: 'error.main',
                      cursor: !isEditing ? 'pointer' : 'default',
                      transition: 'background-color 0.3s'
                    }}
                    onMouseEnter={() => setFocusedField('meter_id')}
                    onMouseLeave={() => setFocusedField(null)}
                    onDoubleClick={() => {
                      if (!isEditing) {
                        setIsEditing(true);
                        setFieldToFocus('meter_id');
                      }
                    }}
                    >
                      <Typography variant="body2" color="error.main" gutterBottom>
                        <strong>METER ID *</strong>
                      </Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          variant="outlined"
                          value={editedData?.meter_id?.value || editedData?.meter_id || ''}
                          onChange={(e) => {
                            const newData = JSON.parse(JSON.stringify(editedData));
                            const upperValue = e.target.value.toUpperCase();
                            // Update in the proper embedded format
                            if (typeof newData.meter_id === 'object' && 'value' in newData.meter_id) {
                              newData.meter_id.value = upperValue;
                              newData.meter_id.confidence = 1.0;
                            } else {
                              newData.meter_id = { value: upperValue, confidence: 1.0 };
                            }
                            setEditedData(newData);
                          }}
                          autoFocus={fieldToFocus === 'meter_id'}
                          inputRef={(input) => {
                            if (fieldToFocus === 'meter_id' && input) {
                              input.focus();
                              input.select();
                              setFieldToFocus(null);
                            }
                          }}
                          sx={{
                            '& .MuiInputBase-input': {
                              fontWeight: 'bold',
                              fontSize: '1.1rem'
                            }
                          }}
                        />
                      ) : (
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {getFieldValue(localExtractedData?.meter_id).value || 'MISSING'}
                          {getFieldValue(localExtractedData?.meter_id).confidence !== null &&
                            renderConfidenceChip(getFieldValue(localExtractedData?.meter_id).confidence)}
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  {/* Product Type - CRITICAL */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{
                      p: 2,
                      bgcolor: focusedField === 'product_type' ? 'action.hover' : 'background.paper',
                      borderRadius: 1,
                      border: '2px solid',
                      borderColor: 'error.main',
                      cursor: !isEditing ? 'pointer' : 'default',
                      transition: 'background-color 0.3s'
                    }}
                    onMouseEnter={() => setFocusedField('product_type')}
                    onMouseLeave={() => setFocusedField(null)}
                    onDoubleClick={() => {
                      if (!isEditing) {
                        setIsEditing(true);
                        setFieldToFocus('product_type');
                      }
                    }}
                    >
                      <Typography variant="body2" color="error.main" gutterBottom>
                        <strong>PRODUCT TYPE *</strong>
                      </Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          variant="outlined"
                          value={editedData?.product_type?.value || editedData?.product_type || ''}
                          onChange={(e) => {
                            const newData = JSON.parse(JSON.stringify(editedData));
                            const upperValue = e.target.value.toUpperCase();
                            // Update in the proper embedded format
                            if (typeof newData.product_type === 'object' && 'value' in newData.product_type) {
                              newData.product_type.value = upperValue;
                              newData.product_type.confidence = 1.0;
                            } else {
                              newData.product_type = { value: upperValue, confidence: 1.0 };
                            }
                            setEditedData(newData);
                          }}
                          autoFocus={fieldToFocus === 'product_type'}
                          inputRef={(input) => {
                            if (fieldToFocus === 'product_type' && input) {
                              input.focus();
                              input.select();
                              setFieldToFocus(null);
                            }
                          }}
                          sx={{
                            '& .MuiInputBase-input': {
                              fontWeight: 'bold',
                              fontSize: '1.1rem'
                            }
                          }}
                        />
                      ) : (
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {getFieldValue(localExtractedData?.product_type).value || 'MISSING'}
                          {getFieldValue(localExtractedData?.product_type).confidence !== null &&
                            renderConfidenceChip(getFieldValue(localExtractedData?.product_type).confidence)}
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  {/* Meter Factor - CRITICAL - Format Specific (hidden for SGS; shown per-flow below) */}
                  {!isSgsReport() && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{
                      p: 2,
                      bgcolor: focusedField === 'meter_factor' ? 'action.hover' : 'background.paper',
                      borderRadius: 1,
                      border: '2px solid',
                      borderColor: 'error.main',
                      cursor: !isEditing ? 'pointer' : 'default',
                      transition: 'background-color 0.3s'
                    }}
                    onMouseEnter={() => setFocusedField('meter_factor')}
                    onMouseLeave={() => setFocusedField(null)}
                    onDoubleClick={() => {
                      if (!isEditing) {
                        setIsEditing(true);
                        setFieldToFocus('meter_factor');
                      }
                    }}
                    >
                      <Typography variant="body2" color="error.main" gutterBottom>
                        <strong>METER FACTOR *</strong>
                        {(() => {
                          const averagesField = getFieldValue(localExtractedData?.averages);
                          const averages = averagesField.value || localExtractedData?.averages;
                          const hasFlowRateAverages = (averages as any)?.flow_rate_averages;
                          const avgMeterFactor = getFieldValue((averages as any)?.avg_meter_factor);
                          const formatValue = getFieldValue((localExtractedData as any)?.format).value;

                          if (hasFlowRateAverages) {
                            return <Chip label="See Multi-Flow Below" size="small" color="error" sx={{ ml: 1 }} />;
                          } else if ((formatValue === 'PMEC' || avgMeterFactor.value)) {
                            return <Chip label="Using avg_meter_factor" size="small" color="warning" sx={{ ml: 1 }} />;
                          }
                          return null;
                        })()}
                      </Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          variant="outlined"
                          value={(() => {
                            // For editing, check multiple possible sources - SAME ORDER AS DISPLAY
                            const averages = editedData?.averages;
                            // Check averages.avg_meter_factor first (matches display logic)
                            if ((averages as any)?.avg_meter_factor?.value) {
                              return (averages as any).avg_meter_factor.value;
                            } else if ((averages as any)?.avg_meter_factor && typeof (averages as any).avg_meter_factor === 'string') {
                              return (averages as any).avg_meter_factor;
                            }
                            // Then check standard meter_factor
                            if (editedData?.meter_factor?.value) {
                              return editedData.meter_factor.value;
                            } else if (typeof editedData?.meter_factor === 'string') {
                              return editedData.meter_factor;
                            }
                            return '';
                          })()}
                          onChange={(e) => {
                            const newData = JSON.parse(JSON.stringify(editedData));
                            const value = e.target.value;

                            // Determine which field to update based on the report format
                            const processingType = reportData.processing_type || '';
                            const formatValue = newData.format?.value || newData.format || '';
                            const hasAvgMeterFactor = (newData.averages as any)?.avg_meter_factor !== undefined;

                            // For PMEC/SGS or reports with avg_meter_factor, update BOTH locations
                            if (processingType === 'pmec' || processingType === 'sgs' || formatValue === 'PMEC' || hasAvgMeterFactor) {
                              // Update avg_meter_factor (primary for these formats)
                              if (!newData.averages) {
                                newData.averages = {};
                              }
                              if (typeof (newData.averages as any).avg_meter_factor === 'object') {
                                (newData.averages as any).avg_meter_factor.value = value;
                                (newData.averages as any).avg_meter_factor.confidence = 1.0;
                              } else {
                                (newData.averages as any).avg_meter_factor = { value: value, confidence: 1.0 };
                              }

                              // Also update standard meter_factor for consistency
                              if (typeof newData.meter_factor === 'object' && 'value' in newData.meter_factor) {
                                newData.meter_factor.value = value;
                                newData.meter_factor.confidence = 1.0;
                              } else {
                                newData.meter_factor = { value: value, confidence: 1.0 };
                              }
                            } else {
                              // Standard format - only update meter_factor
                              if (typeof newData.meter_factor === 'object' && 'value' in newData.meter_factor) {
                                newData.meter_factor.value = value;
                                newData.meter_factor.confidence = 1.0;
                              } else {
                                newData.meter_factor = { value: value, confidence: 1.0 };
                              }
                            }

                            console.log('Updated meter_factor fields:', {
                              meter_factor: newData.meter_factor,
                              avg_meter_factor: (newData.averages as any)?.avg_meter_factor,
                              processingType,
                              formatValue
                            });

                            setEditedData(newData);
                          }}
                          autoFocus={fieldToFocus === 'meter_factor'}
                          inputRef={(input) => {
                            if (fieldToFocus === 'meter_factor' && input) {
                              input.focus();
                              input.select();
                              setFieldToFocus(null);
                            }
                          }}
                          sx={{
                            '& .MuiInputBase-input': {
                              fontWeight: 'bold',
                              fontSize: '1.1rem'
                            }
                          }}
                        />
                      ) : (
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {(() => {
                            const averagesField = getFieldValue(localExtractedData?.averages);
                          const averages = averagesField.value || localExtractedData?.averages;
                            const avgMF = getFieldValue((averages as any)?.avg_meter_factor);
                            const standardMF = getFieldValue(extractedData?.meter_factor);

                            const displayValue = avgMF.value || standardMF.value || 'MISSING';
                            const confidence = avgMF.confidence !== null ? avgMF.confidence : standardMF.confidence;

                            return (
                              <>
                                {displayValue}
                                {confidence !== null && renderConfidenceChip(confidence)}
                              </>
                            );
                          })()}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  )}

                  {/* Test Date - CRITICAL */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{
                      p: 2,
                      bgcolor: focusedField === 'test_date' ? 'action.hover' : 'background.paper',
                      borderRadius: 1,
                      border: '2px solid',
                      borderColor: 'error.main',
                      cursor: !isEditing ? 'pointer' : 'default',
                      transition: 'background-color 0.3s'
                    }}
                    onMouseEnter={() => setFocusedField('test_date')}
                    onMouseLeave={() => setFocusedField(null)}
                    onDoubleClick={() => {
                      if (!isEditing) {
                        setIsEditing(true);
                        setFieldToFocus('test_date');
                      }
                    }}
                    >
                      <Typography variant="body2" color="error.main" gutterBottom>
                        <strong>TEST DATE *</strong>
                      </Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          variant="outlined"
                          type="date"
                          value={convertDateToISO(editedData?.test_date?.value || editedData?.test_date || '')}
                          onChange={(e) => {
                            const newData = JSON.parse(JSON.stringify(editedData));
                            // Update in the proper embedded format
                            if (typeof newData.test_date === 'object' && 'value' in newData.test_date) {
                              newData.test_date.value = e.target.value;
                              newData.test_date.confidence = 1.0;
                            } else {
                              newData.test_date = { value: e.target.value, confidence: 1.0 };
                            }
                            setEditedData(newData);
                          }}
                          autoFocus={fieldToFocus === 'test_date'}
                          inputRef={(input) => {
                            if (fieldToFocus === 'test_date' && input) {
                              input.focus();
                              input.select();
                              setFieldToFocus(null);
                            }
                          }}
                          sx={{
                            '& .MuiInputBase-input': {
                              fontWeight: 'bold',
                              fontSize: '1.1rem'
                            }
                          }}
                        />
                      ) : (
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {getFieldValue(localExtractedData?.test_date).value || 'MISSING'}
                          {getFieldValue(localExtractedData?.test_date).confidence !== null &&
                            renderConfidenceChip(getFieldValue(localExtractedData?.test_date).confidence)}
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  {/* Flow Rate (REGRESSION FIELD) */}
                  {!isSgsReport() && (localExtractedData?.flow_rate || isEditing) && (
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{
                        p: 2,
                        bgcolor: focusedField === 'flow_rate' ? 'action.hover' : 'background.paper',
                        borderRadius: 1,
                        border: '2px solid',
                        borderColor: 'primary.main',
                        cursor: !isEditing ? 'pointer' : 'default',
                        transition: 'background-color 0.3s'
                      }}
                      onMouseEnter={() => setFocusedField('flow_rate')}
                      onMouseLeave={() => setFocusedField(null)}
                      onDoubleClick={() => {
                        if (!isEditing) {
                          setIsEditing(true);
                          setFieldToFocus('flow_rate');
                        }
                      }}
                      >
                        <Typography variant="body2" color="primary.main" gutterBottom>
                          <strong>FLOW RATE 📈</strong> (Regression)
                        </Typography>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            value={editedData?.flow_rate?.value || editedData?.flow_rate || ''}
                            autoFocus={fieldToFocus === 'flow_rate'}
                            inputRef={(input) => {
                              if (fieldToFocus === 'flow_rate' && input) {
                                input.focus();
                                input.select();
                                setFieldToFocus(null);
                              }
                            }}
                            onChange={(e) => {
                              const newData = { ...editedData };
                              if (typeof newData.flow_rate === 'object') {
                                newData.flow_rate = { ...newData.flow_rate, value: e.target.value };
                              } else {
                                newData.flow_rate = e.target.value;
                              }
                              setEditedData(newData);
                            }}
                            sx={{
                              '& .MuiInputBase-input': {
                                fontWeight: 'bold'
                              }
                            }}
                          />
                        ) : (
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {getFieldValue(localExtractedData?.flow_rate).value || 'N/A'}
                            {getFieldValue(localExtractedData?.flow_rate).confidence !== null &&
                              renderConfidenceChip(getFieldValue(localExtractedData?.flow_rate).confidence)}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  )}

                  {/* Meter Temperature (REGRESSION FIELD) - hidden for SGS; shown per-flow below */}
                  {!isSgsReport() && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{
                      p: 2,
                      bgcolor: focusedField === 'meter_temperature' ? 'action.hover' : 'background.paper',
                      borderRadius: 1,
                      border: '2px solid',
                      borderColor: 'primary.main',
                      cursor: !isEditing ? 'pointer' : 'default',
                      transition: 'background-color 0.3s'
                    }}
                    onMouseEnter={() => setFocusedField('meter_temperature')}
                    onMouseLeave={() => setFocusedField(null)}
                    onDoubleClick={() => {
                      if (!isEditing) {
                        setIsEditing(true);
                        setFieldToFocus('meter_temperature');
                      }
                    }}
                    >
                      <Typography variant="body2" color="primary.main" gutterBottom>
                        <strong>METER TEMP 📈</strong> (Regression)
                      </Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          variant="outlined"
                          value={editedData?.meter_temperature?.value || editedData?.meter_temperature || ''}
                          onChange={(e) => {
                            const newData = JSON.parse(JSON.stringify(editedData));
                            if (typeof newData.meter_temperature === 'object' && 'value' in newData.meter_temperature) {
                              newData.meter_temperature.value = e.target.value;
                              newData.meter_temperature.confidence = 1.0;
                            } else {
                              newData.meter_temperature = { value: e.target.value, confidence: 1.0 };
                            }
                            setEditedData(newData);
                          }}
                          autoFocus={fieldToFocus === 'meter_temperature'}
                          inputRef={(input) => {
                            if (fieldToFocus === 'meter_temperature' && input) {
                              input.focus();
                              input.select();
                              setFieldToFocus(null);
                            }
                          }}
                          sx={{
                            '& .MuiInputBase-input': {
                              fontWeight: 'bold',
                              fontSize: '1.1rem'
                            }
                          }}
                        />
                      ) : (
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {getFieldValue(localExtractedData?.meter_temperature).value || 'N/A'}
                          {getFieldValue(localExtractedData?.meter_temperature).confidence !== null &&
                            renderConfidenceChip(getFieldValue(localExtractedData?.meter_temperature).confidence)}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  )}

                  {/* API Gravity / Density (REGRESSION FIELD) */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{
                      p: 2,
                      bgcolor: focusedField === 'api_gravity' ? 'action.hover' : 'background.paper',
                      borderRadius: 1,
                      border: '2px solid',
                      borderColor: 'primary.main',
                      cursor: !isEditing ? 'pointer' : 'default',
                      transition: 'background-color 0.3s'
                    }}
                    onMouseEnter={() => setFocusedField('api_gravity')}
                    onMouseLeave={() => setFocusedField(null)}
                    onDoubleClick={() => {
                      if (!isEditing) {
                        setIsEditing(true);
                        setFieldToFocus('api_gravity');
                      }
                    }}
                    >
                      <Typography variant="body2" color="primary.main" gutterBottom>
                        <strong>API GRAVITY 📈</strong> (Regression)
                      </Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          variant="outlined"
                          value={(() => {
                            // Mirror the display: show api_gravity, else density; never
                            // render a raw {value,confidence} object as "[object Object]".
                            const v = getFieldValue(editedData?.api_gravity).value ||
                                      getFieldValue(editedData?.density).value;
                            if (v === null || v === undefined || typeof v === 'object') return '';
                            return v;
                          })()}
                          onChange={(e) => {
                            const newData = JSON.parse(JSON.stringify(editedData));
                            const val = e.target.value;
                            // Edit whichever field is the DISPLAYED source: api_gravity if
                            // it has a value, otherwise density (SGS carries it in density).
                            // These are different quantities, so never overwrite both.
                            const apiHasVal = getFieldValue(newData.api_gravity).value;
                            const densityHasVal = getFieldValue(newData.density).value;
                            const targetKey = apiHasVal ? 'api_gravity'
                              : (densityHasVal ? 'density' : 'api_gravity');
                            if (newData[targetKey] && typeof newData[targetKey] === 'object' && 'value' in newData[targetKey]) {
                              newData[targetKey].value = val;
                              newData[targetKey].confidence = 1.0;
                            } else {
                              newData[targetKey] = { value: val, confidence: 1.0 };
                            }
                            setEditedData(newData);
                          }}
                          autoFocus={fieldToFocus === 'api_gravity'}
                          inputRef={(input) => {
                            if (fieldToFocus === 'api_gravity' && input) {
                              input.focus();
                              input.select();
                              setFieldToFocus(null);
                            }
                          }}
                          sx={{
                            '& .MuiInputBase-input': {
                              fontWeight: 'bold',
                              fontSize: '1.1rem'
                            }
                          }}
                        />
                      ) : (
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {(() => {
                            const v = getFieldValue(localExtractedData?.api_gravity).value ||
                                      getFieldValue(localExtractedData?.density).value;
                            if (v === null || v === undefined || v === '') return 'N/A';
                            // Never render a raw object (shows as "[object Object]");
                            // fall back to a readable string if a nested shape slipped in.
                            return typeof v === 'object' ? JSON.stringify(v) : v;
                          })()}
                          {(getFieldValue(localExtractedData?.api_gravity).confidence !== null ||
                            getFieldValue(localExtractedData?.density).confidence !== null) &&
                            renderConfidenceChip(
                              getFieldValue(localExtractedData?.api_gravity).confidence ||
                              getFieldValue(localExtractedData?.density).confidence
                            )}
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  {/* Meter Pressure (REGRESSION FIELD - Optional) - hidden for SGS; shown per-flow below */}
                  {!isSgsReport() && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{
                      p: 2,
                      bgcolor: focusedField === 'meter_pressure' ? 'action.hover' : 'background.paper',
                      borderRadius: 1,
                      border: '2px solid',
                      borderColor: 'info.main',
                      cursor: !isEditing ? 'pointer' : 'default',
                      transition: 'background-color 0.3s'
                    }}
                    onMouseEnter={() => setFocusedField('meter_pressure')}
                    onMouseLeave={() => setFocusedField(null)}
                    onDoubleClick={() => {
                      if (!isEditing) {
                        setIsEditing(true);
                        setFieldToFocus('meter_pressure');
                      }
                    }}
                    >
                      <Typography variant="body2" color="info.main" gutterBottom>
                        <strong>METER PRESSURE 📈</strong> (Optional)
                      </Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          variant="outlined"
                          value={editedData?.meter_pressure?.value || editedData?.meter_pressure || ''}
                          onChange={(e) => {
                            const newData = JSON.parse(JSON.stringify(editedData));
                            if (typeof newData.meter_pressure === 'object' && 'value' in newData.meter_pressure) {
                              newData.meter_pressure.value = e.target.value;
                              newData.meter_pressure.confidence = 1.0;
                            } else {
                              newData.meter_pressure = { value: e.target.value, confidence: 1.0 };
                            }
                            setEditedData(newData);
                          }}
                          autoFocus={fieldToFocus === 'meter_pressure'}
                          inputRef={(input) => {
                            if (fieldToFocus === 'meter_pressure' && input) {
                              input.focus();
                              input.select();
                              setFieldToFocus(null);
                            }
                          }}
                          sx={{
                            '& .MuiInputBase-input': {
                              fontWeight: 'bold',
                              fontSize: '1.1rem'
                            }
                          }}
                        />
                      ) : (
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {getFieldValue(localExtractedData?.meter_pressure).value || 'N/A'}
                          {getFieldValue(localExtractedData?.meter_pressure).confidence !== null &&
                            renderConfidenceChip(getFieldValue(localExtractedData?.meter_pressure).confidence)}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  )}

                  {/* SGS key fields: Meter Under Test serial (tracked in
                      MeterFactorHistory) and Adjustments */}
                  {isSgsReport() && renderKeyScalarCell(
                    'METER SERIAL',
                    'meter_under_test.serial_number',
                    (() => {
                      const m = (localExtractedData as any)?.meter_under_test;
                      const inner = (m && typeof m === 'object' && m.value && typeof m.value === 'object') ? m.value : m;
                      return inner ? inner.serial_number : undefined;
                    })()
                  )}
                  {isSgsReport() && renderKeyScalarCell(
                    'ADJUSTMENTS',
                    'adjustments',
                    (localExtractedData as any)?.adjustments
                  )}

                  {/* SGS: per-flow HIGH/LOW track cards — exactly what each
                      MeterFactorHistory record will contain (one per flow) */}
                  {isSgsReport() && renderSgsFlowTracks()}
                </Grid>

                {/* Regression Fields Info */}
                <Alert severity="info" sx={{ mt: 2 }}>
                  <AlertTitle>📈 Regression Analysis Fields</AlertTitle>
                  <Typography variant="body2">
                    <strong>Required for Regression:</strong> Flow Rate, Meter Temperature, API Gravity/Density
                    <br />
                    <strong>Optional Enhancement:</strong> Meter Pressure (improves accuracy if available)
                    <br />
                    These fields enable predictive analysis to detect meter drift beyond normal operating variance.
                  </Typography>
                </Alert>

                {/* SGS Multi-Flow Section - CRITICAL */}
                {(() => {
                  const flowRateAverages = (localExtractedData?.averages as any)?.flow_rate_averages;

                  if (flowRateAverages && Array.isArray(flowRateAverages)) {
                    return (
                      <Box sx={{ mt: 3, p: 2, bgcolor: 'error.50', borderRadius: 1, border: '2px solid', borderColor: 'error.main' }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'error.main' }}>
                          🔄 SGS MULTI-FLOW CONFIGURATION - CRITICAL FOR METER HISTORY
                        </Typography>
                        <Alert severity="error" sx={{ mb: 2 }}>
                          <AlertTitle>Important: Each flow rate creates a separate control chart!</AlertTitle>
                          Each flow rate below will create a meter-product ID like: <strong>{getFieldValue(extractedData?.meter_id).value || 'METER'}_{getFieldValue(extractedData?.product_type).value || 'PRODUCT'}_FLOWRATE</strong>
                          <br />The <strong>average_meter_factor</strong> from each flow becomes the meter_factor for that flow's control chart.
                        </Alert>
                        <Grid container spacing={2}>
                          {flowRateAverages.map((flow: any, index: number) => {
                            const flowRate = getFieldValue(flow.flow_rate);
                            const avgMeterFactor = getFieldValue(flow.average_meter_factor);
                            const repeatability = getFieldValue(flow.repeatability);

                            return (
                              <Grid item xs={12} sm={6} md={4} key={index}>
                                <Box sx={{
                                  p: 1.5,
                                  bgcolor: 'background.paper',
                                  borderRadius: 1,
                                  border: '2px solid',
                                  borderColor: avgMeterFactor.value ? 'success.main' : 'error.main'
                                }}>
                                  <Typography variant="body2" color="text.secondary">
                                    Flow Rate {index + 1}
                                  </Typography>
                                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: flowRate.value ? 'text.primary' : 'error.main' }}>
                                    {flowRate.value || 'MISSING'} BPH
                                  </Typography>
                                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: avgMeterFactor.value ? 'success.main' : 'error.main' }}>
                                    MF: {avgMeterFactor.value || 'MISSING'}
                                  </Typography>
                                  {repeatability.value && (
                                    <Typography variant="body2">
                                      Rep: {repeatability.value}%
                                    </Typography>
                                  )}
                                  {isEditing && (
                                    <Box sx={{ mt: 1 }}>
                                      <TextField
                                        size="small"
                                        label="Flow Rate (BPH)"
                                        fullWidth
                                        value={(() => {
                                          const flowData = (editedData?.averages as any)?.flow_rate_averages?.[index];
                                          if (flowData?.flow_rate?.value) return flowData.flow_rate.value;
                                          if (flowData?.flow_rate) return flowData.flow_rate;
                                          return '';
                                        })()}
                                        onChange={(e) => {
                                          const newData = { ...editedData };
                                          if (!newData.averages) newData.averages = {};
                                          if (!(newData.averages as any).flow_rate_averages) {
                                            (newData.averages as any).flow_rate_averages = flowRateAverages;
                                          }
                                          if (!(newData.averages as any).flow_rate_averages[index]) {
                                            (newData.averages as any).flow_rate_averages[index] = {};
                                          }
                                          (newData.averages as any).flow_rate_averages[index].flow_rate = { value: e.target.value };
                                          setEditedData(newData);
                                        }}
                                        sx={{ mb: 1 }}
                                      />
                                      <TextField
                                        size="small"
                                        label="Average Meter Factor"
                                        fullWidth
                                        value={(() => {
                                          const flowData = (editedData?.averages as any)?.flow_rate_averages?.[index];
                                          if (flowData?.average_meter_factor?.value) return flowData.average_meter_factor.value;
                                          if (flowData?.average_meter_factor) return flowData.average_meter_factor;
                                          return '';
                                        })()}
                                        onChange={(e) => {
                                          const newData = { ...editedData };
                                          if (!newData.averages) newData.averages = {};
                                          if (!(newData.averages as any).flow_rate_averages) {
                                            (newData.averages as any).flow_rate_averages = flowRateAverages;
                                          }
                                          if (!(newData.averages as any).flow_rate_averages[index]) {
                                            (newData.averages as any).flow_rate_averages[index] = {};
                                          }
                                          (newData.averages as any).flow_rate_averages[index].average_meter_factor = { value: e.target.value };
                                          setEditedData(newData);
                                        }}
                                        error={!avgMeterFactor.value}
                                        helperText={!avgMeterFactor.value ? 'Required' : ''}
                                      />
                                    </Box>
                                  )}
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Box>
                    );
                  }
                  return null;
                })()}

                <Alert severity="info" sx={{ mt: 2 }}>
                  <AlertTitle>Critical Field Mapping by Format</AlertTitle>
                  <Typography variant="body2">
                    <strong>Standard Format:</strong> Uses meter_factor field directly<br />
                    <strong>PMEC Format:</strong> Uses averages → avg_meter_factor (this overrides the standard meter_factor)<br />
                    <strong>SGS Multi-Flow:</strong> Uses averages → flow_rate_averages → [each flow] → average_meter_factor<br />
                    <br />
                    ⚠️ The backend automatically detects and uses the correct field based on format!
                  </Typography>
                </Alert>
              </CardContent>
            </Card>

            {/* ALL EXTRACTED FIELDS - Complete Data Review */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>All Extracted Fields</Typography>
                <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block', mb: 2 }}>
                  Review and edit any field extracted by AI - all fields are shown below
                </Typography>
                <Grid container spacing={2}>
                  {/* Display all root-level fields first */}
                  {localExtractedData && Object.keys(localExtractedData)
                    .filter(field =>
                      field !== 'test_runs' &&
                      field !== 'averages' &&
                      field !== 'corrections' &&
                      field !== 'final_results' &&
                      field !== 'additional_fields' &&  // Don't render additional_fields as single field
                      field !== 'bounding_boxes' &&
                      field !== 'source_page' &&
                      field !== 'source_document' &&
                      field !== 'source_image'
                    )
                    .map((field) => {
                      const fieldData = (localExtractedData as any)[field];
                      return (
                        <Grid item xs={12} sm={6} md={4} key={field}>
                          {renderFieldWithConfidence(
                            field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                            fieldData,
                            field
                          )}
                        </Grid>
                      );
                    })}
                </Grid>
              </CardContent>
            </Card>

            {/* Test Runs - EDITABLE */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Test Runs (All Fields)</Typography>
                {testRuns.length === 0 ? (
                  <Typography color="text.secondary">No test runs found</Typography>
                ) : (
                  testRuns.map((run: any, index: number) => (
                    <Card key={index} sx={{ mb: 2, p: 2, backgroundColor: 'grey.50' }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Run {getFieldValue(run.run_number).value || index + 1}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={4}>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              label="Pulses Run"
                              value={getFieldValue(run.pulses_run).value || ''}
                              onChange={(e) => handleTestRunChange(index, 'pulses_run', e.target.value)}
                            />
                          ) : (
                            renderFieldWithConfidence('Pulses Run', run.pulses_run, `test_runs.${index}.pulses_run`)
                          )}
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              label="Pulses Returned"
                              value={getFieldValue(run.pulses_returned).value || ''}
                              onChange={(e) => handleTestRunChange(index, 'pulses_returned', e.target.value)}
                            />
                          ) : (
                            renderFieldWithConfidence('Pulses Returned', run.pulses_returned, `test_runs.${index}.pulses_returned`)
                          )}
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              label="Prover Temperature"
                              value={getFieldValue(run.prover_temperature).value || ''}
                              onChange={(e) => handleTestRunChange(index, 'prover_temperature', e.target.value)}
                            />
                          ) : (
                            renderFieldWithConfidence('Prover Temp', run.prover_temperature, `test_runs.${index}.prover_temperature`)
                          )}
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              label="Meter Temperature"
                              value={getFieldValue(run.meter_temperature).value || ''}
                              onChange={(e) => handleTestRunChange(index, 'meter_temperature', e.target.value)}
                            />
                          ) : (
                            renderFieldWithConfidence('Meter Temp', run.meter_temperature, `test_runs.${index}.meter_temperature`)
                          )}
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              label="Prover Pressure"
                              value={getFieldValue(run.prover_pressure).value || ''}
                              onChange={(e) => handleTestRunChange(index, 'prover_pressure', e.target.value)}
                            />
                          ) : (
                            renderFieldWithConfidence('Prover Pressure', run.prover_pressure, `test_runs.${index}.prover_pressure`)
                          )}
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              label="Meter Pressure"
                              value={getFieldValue(run.meter_pressure).value || ''}
                              onChange={(e) => handleTestRunChange(index, 'meter_pressure', e.target.value)}
                            />
                          ) : (
                            renderFieldWithConfidence('Meter Pressure', run.meter_pressure, `test_runs.${index}.meter_pressure`)
                          )}
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              label="Deliver to Can"
                              value={getFieldValue(run.deliver_to_can).value || ''}
                              onChange={(e) => handleTestRunChange(index, 'deliver_to_can', e.target.value)}
                            />
                          ) : (
                            renderFieldWithConfidence('Deliver to Can', run.deliver_to_can, `test_runs.${index}.deliver_to_can`)
                          )}
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              label="Final Meter Reading"
                              value={getFieldValue(run.final_meter_reading).value || ''}
                              onChange={(e) => handleTestRunChange(index, 'final_meter_reading', e.target.value)}
                            />
                          ) : (
                            renderFieldWithConfidence('Final Reading', run.final_meter_reading, `test_runs.${index}.final_meter_reading`)
                          )}
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              label="CTL"
                              value={getFieldValue(run.temp_corr_fact_on_liquid).value || ''}
                              onChange={(e) => handleTestRunChange(index, 'temp_corr_fact_on_liquid', e.target.value)}
                            />
                          ) : (
                            renderFieldWithConfidence('CTL', run.temp_corr_fact_on_liquid, `test_runs.${index}.temp_corr_fact_on_liquid`)
                          )}
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              label="CTSP"
                              value={getFieldValue(run.temp_corr_fact_on_steel).value || ''}
                              onChange={(e) => handleTestRunChange(index, 'temp_corr_fact_on_steel', e.target.value)}
                            />
                          ) : (
                            renderFieldWithConfidence('CTSP', run.temp_corr_fact_on_steel, `test_runs.${index}.temp_corr_fact_on_steel`)
                          )}
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              label="Combined Corr Factor"
                              value={getFieldValue(run.combined_corr_fact).value || ''}
                              onChange={(e) => handleTestRunChange(index, 'combined_corr_fact', e.target.value)}
                            />
                          ) : (
                            renderFieldWithConfidence('Combined CF', run.combined_corr_fact, `test_runs.${index}.combined_corr_fact`)
                          )}
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              label="Meter Factor Gross"
                              value={getFieldValue(run.meter_factor_gross).value || ''}
                              onChange={(e) => handleTestRunChange(index, 'meter_factor_gross', e.target.value)}
                            />
                          ) : (
                            renderFieldWithConfidence('MF Gross', run.meter_factor_gross, `test_runs.${index}.meter_factor_gross`)
                          )}
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              label="Meter Factor Net"
                              value={getFieldValue(run.meter_factor_net).value || ''}
                              onChange={(e) => handleTestRunChange(index, 'meter_factor_net', e.target.value)}
                            />
                          ) : (
                            renderFieldWithConfidence('MF Net', run.meter_factor_net, `test_runs.${index}.meter_factor_net`)
                          )}
                        </Grid>
                      </Grid>
                    </Card>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Averages Section - Show ALL average fields */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Averages (All Fields)</Typography>
                <Grid container spacing={2}>
                  {/* Check both direct averages object and nested value */}
                  {(() => {
                    const averagesData = (localExtractedData as any)?.averages;
                    if (!averagesData) return null;

                    // Handle both formats: direct object or {value: {...}, confidence: ...}
                    const averageFields = averagesData.value || averagesData;

                    if (typeof averageFields === 'object' && averageFields !== null) {
                      return Object.keys(averageFields).map((key: string) => {
                        if (key === 'confidence' || key === 'value') return null;

                        const fieldData = averageFields[key];

                        // Debug logging
                        console.log(`Processing averages.${key}:`, fieldData);

                        // Special handling for flow_rate_averages (SGS format)
                        if (key === 'flow_rate_averages') {
                          // Handle both array and DynamoDB L format
                          let flowArray = fieldData;
                          if (fieldData && typeof fieldData === 'object' && 'L' in fieldData) {
                            flowArray = fieldData.L;
                          }

                          if (Array.isArray(flowArray)) {
                            return (
                              <Grid item xs={12} key={key}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  Flow Rate Averages
                                </Typography>
                                {flowArray.map((flow: any, index: number) => {
                                  // Handle DynamoDB M format
                                  let flowData = flow;
                                  if (flow && typeof flow === 'object' && 'M' in flow) {
                                    flowData = flow.M;
                                  }

                                  // Extract values from nested format
                                  const getFlowValue = (field: any) => {
                                    if (!field) return null;
                                    if (typeof field === 'string' || typeof field === 'number') return field;
                                    if (field.S) return field.S;
                                    if (field.N) return field.N;
                                    if (field.value) {
                                      if (typeof field.value === 'object' && field.value.S) return field.value.S;
                                      return field.value;
                                    }
                                    return null;
                                  };

                                  const flowRate = getFlowValue(flowData?.flow_rate) || 'Unknown';
                                  const avgMeterFactor = getFlowValue(flowData?.average_meter_factor) || '-';
                                  const repeatability = getFlowValue(flowData?.repeatability) || '-';

                                  return (
                                    <Box key={index} sx={{ pl: 2, mb: 1 }}>
                                      <Typography variant="body2">
                                        Flow {flowRate} LPM: MF={avgMeterFactor}, Rep={repeatability}
                                      </Typography>
                                    </Box>
                                  );
                                })}
                              </Grid>
                            );
                          }
                        }

                        // Skip rendering if fieldData is an object with L (list) or M (map) wrapper
                        if (fieldData && typeof fieldData === 'object' && ('L' in fieldData || 'M' in fieldData)) {
                          console.warn(`Skipping rendering of complex object for averages.${key}`, fieldData);
                          return null;
                        }

                        return (
                          <Grid item xs={12} sm={6} md={4} key={key}>
                            {renderFieldWithConfidence(
                              key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                              fieldData,
                              `averages.${key}`
                            )}
                          </Grid>
                        );
                      });
                    }
                    return null;
                  })()}

                  {/* If no averages data exists, show message */}
                  {!(localExtractedData as any)?.averages && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        No averages data found in extraction
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* Corrections Section - Show ALL correction factors */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Correction Factors (All Fields)</Typography>
                <Grid container spacing={2}>
                  {(() => {
                    const correctionsData = (localExtractedData as any)?.corrections;
                    if (!correctionsData) return null;

                    const correctionFields = correctionsData.value || correctionsData;

                    if (typeof correctionFields === 'object' && correctionFields !== null) {
                      return Object.keys(correctionFields).map((key: string) => {
                        if (key === 'confidence' || key === 'value') return null;

                        const fieldData = correctionFields[key];

                        // Skip rendering if fieldData is an object with L (list) or M (map) wrapper
                        if (fieldData && typeof fieldData === 'object' && ('L' in fieldData || 'M' in fieldData)) {
                          console.warn(`Skipping rendering of complex object for corrections.${key}`, fieldData);
                          return null;
                        }

                        return (
                          <Grid item xs={12} sm={6} md={4} key={key}>
                            {renderFieldWithConfidence(
                              key.toUpperCase(),
                              fieldData,
                              `corrections.${key}`
                            )}
                          </Grid>
                        );
                      });
                    }
                    return null;
                  })()}

                  {!(localExtractedData as any)?.corrections && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        No corrections data found in extraction
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* Final Results Section - Show ALL result fields */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Final Results (All Fields)</Typography>
                <Grid container spacing={2}>
                  {(() => {
                    const finalResultsData = (localExtractedData as any)?.final_results;
                    if (!finalResultsData) return null;

                    const resultFields = finalResultsData.value || finalResultsData;

                    if (typeof resultFields === 'object' && resultFields !== null) {
                      return Object.keys(resultFields).map((key: string) => {
                        if (key === 'confidence' || key === 'value') return null;

                        const fieldData = resultFields[key];
                        return (
                          <Grid item xs={12} sm={6} md={4} key={key}>
                            {renderFieldWithConfidence(
                              key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                              fieldData,
                              `final_results.${key}`
                            )}
                          </Grid>
                        );
                      });
                    }
                    return null;
                  })()}

                  {!(localExtractedData as any)?.final_results && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        No final results data found in extraction
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* Additional Fields from additional_fields object */}
            {(localExtractedData as any)?.additional_fields && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Additional Information</Typography>
                  <Grid container spacing={2}>
                    {(() => {
                      const additionalFieldsData = getFieldValue((localExtractedData as any).additional_fields);
                      const additionalFields = additionalFieldsData.value || additionalFieldsData;

                      if (typeof additionalFields === 'object' && additionalFields !== null) {
                        // Handle DynamoDB M wrapper if present
                        let fields = additionalFields;
                        if ('M' in fields) {
                          fields = fields.M;
                        }

                        return Object.keys(fields).map((key: string) => {
                          if (key === 'confidence' || key === 'value') return null;

                          // Get the field and extract its value properly
                          const fieldData = fields[key];

                          return (
                            <Grid item xs={12} sm={6} md={4} key={key}>
                              {renderFieldWithConfidence(
                                key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                                fieldData,
                                `additional_fields.${key}`
                              )}
                            </Grid>
                          );
                        });
                      }
                      return null;
                    })()}
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Additional/Unknown Fields Section - Catch any fields we might have missed */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Other Fields</Typography>
                <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block', mb: 2 }}>
                  Any other fields found in the extraction that don't fit standard categories
                </Typography>
                <Grid container spacing={2}>
                  {localExtractedData && Object.keys(localExtractedData)
                    .filter(field => {
                      // Find fields not already shown
                      const standardFields = ['test_runs', 'averages', 'corrections', 'final_results',
                                            'bounding_boxes', 'source_page', 'source_document', 'source_image',
                                            'additional_fields'];
                      const knownRootFields = ['test_date', 'test_id', 'meter_id', 'meter_factor',
                                              'previous_meter_factor', 'new_meter_factor', 'k_factor',
                                              'product_type', 'customer', 'location', 'company_name',
                                              'batch_id', 'technician', 'prover_serial', 'api_gravity',
                                              'flow_rate', 'meter_temperature', 'meter_pressure',
                                              'meter_repeatability', 'change_percentage'];

                      return !standardFields.includes(field) && !knownRootFields.includes(field);
                    })
                    .map((field) => {
                      const fieldData = (localExtractedData as any)[field];
                      return (
                        <Grid item xs={12} sm={6} md={4} key={field}>
                          {renderFieldWithConfidence(
                            field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                            fieldData,
                            field
                          )}
                        </Grid>
                      );
                    })}

                  {/* If no additional fields */}
                  {localExtractedData && Object.keys(localExtractedData)
                    .filter(field => {
                      const standardFields = ['test_runs', 'averages', 'corrections', 'final_results',
                                            'bounding_boxes', 'source_page', 'source_document', 'source_image',
                                            'additional_fields'];
                      const knownRootFields = ['test_date', 'test_id', 'meter_id', 'meter_factor',
                                              'previous_meter_factor', 'new_meter_factor', 'k_factor',
                                              'product_type', 'customer', 'location', 'company_name',
                                              'batch_id', 'technician', 'prover_serial', 'api_gravity',
                                              'flow_rate', 'meter_temperature', 'meter_pressure',
                                              'meter_repeatability', 'change_percentage'];

                      return !standardFields.includes(field) && !knownRootFields.includes(field);
                    }).length === 0 && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        No additional fields found
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
            </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Save Status Snackbar */}
      <Snackbar
        open={saveStatus.show}
        autoHideDuration={6000}
        onClose={() => setSaveStatus({ ...saveStatus, show: false })}
      >
        <Alert
          onClose={() => setSaveStatus({ ...saveStatus, show: false })}
          severity={saveStatus.severity}
        >
          {saveStatus.message}
        </Alert>
      </Snackbar>

      {/* Add Field Dialog */}
      <Dialog open={addFieldDialog} onClose={() => setAddFieldDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Field</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Section</InputLabel>
              <Select
                value={newFieldSection}
                onChange={(e) => setNewFieldSection(e.target.value)}
                label="Section"
              >
                <MenuItem value="root">Root Level</MenuItem>
                <MenuItem value="averages">Averages</MenuItem>
                <MenuItem value="corrections">Corrections</MenuItem>
                <MenuItem value="final_results">Final Results</MenuItem>
                <MenuItem value="test_runs">Test Runs</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Field Name"
              value={newFieldName}
              onChange={(e) => setNewFieldName(e.target.value)}
              placeholder="e.g., custom_factor, special_note"
              helperText="Use lowercase with underscores for consistency"
            />

            <TextField
              fullWidth
              label="Initial Value"
              value={newFieldValue}
              onChange={(e) => setNewFieldValue(e.target.value)}
              placeholder="Enter initial value"
              helperText="This value can be edited after adding"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddFieldDialog(false);
            setNewFieldName('');
            setNewFieldValue('');
            setNewFieldSection('root');
          }}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (newFieldName.trim()) {
                // Special handling for test_runs - need to specify which run
                if (newFieldSection === 'test_runs') {
                  alert('To add fields to test runs, please use the edit mode and modify individual runs directly.');
                } else {
                  handleAddField(newFieldSection, newFieldName.trim(), newFieldValue);
                  // Also add to edited data if we're in edit mode
                  if (isEditing) {
                    const newEditedData = JSON.parse(JSON.stringify(editedData));
                    if (newFieldSection === 'root') {
                      newEditedData[newFieldName.trim()] = { value: newFieldValue, confidence: 1.0 };
                    } else {
                      if (!newEditedData[newFieldSection]) {
                        newEditedData[newFieldSection] = { value: {}, confidence: 1.0 };
                      }
                      if (!newEditedData[newFieldSection].value) {
                        newEditedData[newFieldSection].value = {};
                      }
                      newEditedData[newFieldSection].value[newFieldName.trim()] = { value: newFieldValue, confidence: 1.0 };
                    }
                    setEditedData(newEditedData);
                  }
                }
                setAddFieldDialog(false);
                setNewFieldName('');
                setNewFieldValue('');
                setNewFieldSection('root');
              }
            }}
            variant="contained"
            disabled={!newFieldName.trim()}
          >
            Add Field
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DataViewerSideBySide;